import type { FeatureCollection } from "geojson";

import type {
  Aar,
  Annotation,
  CustomCompositionState,
  CustomEnemyDetail,
  CustomEnemyList,
  CustomEnemySheet,
  CustomScenario,
  CustomTraining,
  BBox,
  AarTextEvent,
  Briefing,
  ChainInfo,
  ChiefNotice,
  CountrySheet,
  CountrySheetEvent,
  DeclinationState,
  GroupedDocument,
  BriefingEvent,
  CoherenceReport,
  TacticalCoherence,
  Deployment,
  Direx,
  DirexRole,
  EnemyCoa,
  GraphicsCatalog,
  GraphicsImport,
  IncidentChanges,
  ExampleExercise,
  Exercise,
  ExerciseSaved,
  CompositionCandidates,
  CompositionSide,
  CompositionWrite,
  RoleWrite,
  ExerciseAccessView,
  ExerciseParameters,
  ExportCompleteness,
  Force,
  EniRole,
  OdbDetail,
  OdbKind,
  OdbNode,
  OdbPreview,
  OdbRefreshedDossier,
  OdbSide,
  OdbStarter,
  OdbStarterIn,
  OdbSummary,
  OdbVocabulary,
  OpoDraft,
  OpoDraftState,
  OpoDraftSummary,
  OpoImportPreview,
  OpoProposalEvent,
  Frago,
  FragoFieldSpec,
  FragoSummary,
  LayerKey,
  Level,
  LicenceStatus,
  LicenceTerminal,
  MapDetail,
  MapSummary,
  MeasureEnds,
  MeasureKey,
  MissionOption,
  NewIncident,
  Orbat,
  Subordinates,
  Order,
  OrderFieldSpecs,
  OrderLayout,
  OrderVersion,
  OrdersRefreshed,
  CorridorModel,
  Phase,
  PhaseLine,
  PlaceSearchResult,
  Placement,
  RotationResults,
  RotationSummary,
  SituationBoard,
  SituationCell,
  SituationExportRequest,
  SituationFormat,
  SituationPhase,
  SituationSummary,
  Wingo,
  WingoApplied,
  WingoPreview,
  TacticalGraphics,
  TerrainAnalysisRequest,
  TerrainReport,
  WeatherCustom,
  WeatherOptions,
  TransferPreview,
  TransmittedDossier,
  WargameEvent,
  ReceivedApply,
  ReceivedOrder,
  ReceivedSuggestionEvent,
  ReceivedSummary,
  Retex,
  RetexAnalysis,
  RetexEnemy,
  DroneAirspace,
  DronesPreview,
  DronesPreviewRequest,
  Logistics,
  LogisticsAssumptions,
  ReleaseNotes,
  StorageUsage,
  Zone,
  ZoneAxis,
  ZoneCreated,
  ZoneDeleted,
  UnitItems,
  UnitShare,
  AuthEvent,
  AuthSession,
  AuthSessionRow,
  PromotionRequest,
  PromotionStatus,
  PromotionStatusReply,
  AnnouncementRequest,
  AnnouncementStatus,
  AnnouncementSummary,
  BackupRequest,
  BackupStatus,
  BackupSummary,
  PromotionSummary,
  FeedbackAdminFilters,
  FeedbackAdminList,
  FeedbackItem,
  FeedbackMine,
  FeedbackRequest,
  FeedbackStatus,
} from "./types";
import { type IaModel, iaHeaders } from "../ai/iaModel";
import type {
  AccountItem,
  AccountsList,
  AdminRequestRow,
  Approval,
  AuditFilters,
  BatchPreview,
  CreatedAccount,
  IssuedCode,
  PendingFiled,
  UnitAccount,
  UnitAuditView,
  UnitSessionsView,
  UnitSettingsView,
} from "../auth/admin/adminTypes";
import { mountedScope } from "../auth/mounted";
import { lang, onLanguageChange, t, tServer } from "../i18n/i18n";
import { licenceScope } from "../state/licenceScope";
import { emitServerNotices } from "./notices";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Code d'erreur métier (`LICENCE_REQUIRED`, `DEVICE_LIMIT_REACHED`…), quand le serveur en donne un. */
    readonly code?: string,
    /** Autres champs du refus (`TERMINAL_NAME_IN_USE` : nom, nombre et dernière utilisation des postes). */
    readonly detail?: Record<string, unknown>,
    /** Délai conseillé avant une nouvelle demande (en-tête `Retry-After` d'un 429), en millisecondes. */
    readonly retryAfterMs?: number,
    /** Message du serveur tel qu'il l'a rédigé (en français), avant traduction : les refus propres aux zones libres
     *  y sont reconnus (`map/zones/zoneRefusal.ts`) pour désigner l'action attendue. */
    readonly raw?: string,
  ) {
    super(message);
  }
}

/** Code posé sur un refus 413 de l'espace de stockage de la licence (message du serveur « Espace de stockage de la
 *  licence plein / insuffisant… ») : l'interface montre la jauge et désigne l'action attendue. */
export const STORAGE_FULL = "STORAGE_FULL";
/** Code d'un refus 409 « modifié par un autre poste » (incident, point de cellule, hypothèses logistiques, note 3A) :
 *  la saisie est gardée, la fiche relue, l'avis le dit. */
export const CHANGED_ELSEWHERE = "CHANGED_ELSEWHERE";
/** Code d'un refus 412 de l'enregistrement d'un ordre de bataille enregistré entre-temps par un autre poste. */
export const ODB_CHANGED = "ODB_CHANGED";

/** Refus « modifié par un autre poste » (409 `CHANGED_ELSEWHERE`, 412 `ODB_CHANGED`). */
export function changedElsewhere(e: unknown): e is ApiError {
  return e instanceof ApiError && (e.code === CHANGED_ELSEWHERE || e.code === ODB_CHANGED);
}

/** Émis à chaque refus de l'espace de stockage (413) : la jauge de la licence est relue. */
export const STORAGE_FULL_EVENT = "generex:storage-full";
/** Émis à chaque refus 413 `ACCOUNT_QUOTA` (plafond de stockage d'un compte, docs/17 §3.11) : avis avec « Libérer de
 *  l'espace » (jauge détaillée par compte) ; `detail.message` : le refus traduit. */
export const ACCOUNT_QUOTA_EVENT = "generex:account-quota";

/** Délai d'un en-tête `Retry-After` (secondes ou date HTTP), en millisecondes ; null s'il est absent ou illisible. */
export function retryAfterMs(header: string | null | undefined, now = Date.now()): number | null {
  if (!header) return null;
  const value = header.trim();
  if (/^\d+$/.test(value)) return Number(value) * 1000;
  const at = Date.parse(value);
  return Number.isNaN(at) ? null : Math.max(0, at - now);
}

/** Émis quand l'API refuse l'accès faute de licence valide : le portail de licence se met à jour. */
export const LICENCE_REQUIRED_EVENT = "generex:licence-required";
/** Code d'un refus 403 d'écriture pendant la lecture seule d'une licence expirée (décision d'Alexis du 03/10/2026) :
 *  refus définitif jusqu'au renouvellement, jamais réessayé de lui-même. */
export const LICENCE_READ_ONLY = "LICENCE_READ_ONLY";
/** Émis à chaque refus `LICENCE_READ_ONLY` : l'explication est donnée une fois, puis le bandeau est désigné
 *  (`licence/ReadOnlyLicence.tsx`). */
export const LICENCE_READ_ONLY_EVENT = "generex:licence-read-only";

/** Refus d'une écriture par la lecture seule d'une licence expirée. */
export function readOnlyRefused(e: unknown): e is ApiError {
  return e instanceof ApiError && e.code === LICENCE_READ_ONLY;
}

/** Comptes nominatifs (docs/17 §4.5, §4.8, §7.8) : session absente, fermée ou expirée (401 `AUTH_REQUIRED`, avec sa
 *  raison), session verrouillée (401 `SESSION_LOCKED`), session de ce navigateur ouverte pour un autre compte que
 *  celui de l'application montée (409 `ACCOUNT_MISMATCH`). Chacun émet son événement : le portail des comptes pose
 *  sa surcouche, ou recharge la page sans rien rejouer. */
export const AUTH_REQUIRED = "AUTH_REQUIRED";
export const SESSION_LOCKED = "SESSION_LOCKED";
export const ACCOUNT_MISMATCH = "ACCOUNT_MISMATCH";
export const AUTH_REQUIRED_EVENT = "generex:auth-required";
export const SESSION_LOCKED_EVENT = "generex:session-locked";
export const ACCOUNT_MISMATCH_EVENT = "generex:account-mismatch";
/** Compte de l'application montée, contrôlé par la garde sur chaque requête qui résout une session (§4.8). */
export const ACCOUNT_HEADER = "X-Generex-Compte";
/** Relecture automatique d'une page cachée : elle ne compte pas comme une activité pour le serveur (§4.5). */
export const BACKGROUND_HEADER = "X-Generex-Fond";
/** Langue de l'interface du poste (docs/13 « Langue des documents ») : les documents du dossier sortent dans cette
 *  langue (vue traduite d'un document rédigé dans l'autre). Le cookie `generex_langue` (i18n.ts) la porte pour ce
 *  qui part sans en-tête : téléchargement par lien, image d'une carte. */
export const LANGUAGE_HEADER = "X-Generex-Langue";

/** Au changement de langue de l'interface, toutes les requêtes de `queryClient` sont relues (en-tête et cookie déjà
 *  dans la nouvelle langue) : les documents ouverts se rechargent d'eux-mêmes, dans cette langue (docs/13). Rend la
 *  fonction qui arrête ce suivi. */
export function followLanguage(queryClient: { invalidateQueries: () => Promise<unknown> }): () => void {
  return onLanguageChange(() => void queryClient.invalidateQueries());
}

/** Refus qui tient à la session de la personne (verrouillée, fermée, expirée, autre compte) : jamais réessayé de
 *  lui-même, le portail des comptes s'en charge. */
export function sessionRefused(e: unknown): e is ApiError {
  return e instanceof ApiError && (e.code === AUTH_REQUIRED || e.code === SESSION_LOCKED || e.code === ACCOUNT_MISMATCH);
}

/** Propriété et partage (docs/17 §3, §7.8, lot 5) : écriture dans un dossier en lecture seule pour ce compte (403
 *  `READ_ONLY`), action de propriétaire (403 `OWNER_ONLY`), élément employé par des dossiers que le compte ne peut
 *  pas modifier (409 `IN_USE_BY_OTHERS`), plafond de stockage d'un compte atteint (413 `ACCOUNT_QUOTA`), droits d'un
 *  ODB ou d'un terrain d'un autre compte (403 `ITEM_OWNER_ONLY`). Les deux premiers émettent leur événement : le
 *  bandeau de lecture seule du dossier est désigné (`sharing/ReadOnlyBanner.tsx`). */
export const READ_ONLY = "READ_ONLY";
export const OWNER_ONLY = "OWNER_ONLY";
export const IN_USE_BY_OTHERS = "IN_USE_BY_OTHERS";
export const ACCOUNT_QUOTA = "ACCOUNT_QUOTA";
export const ITEM_OWNER_ONLY = "ITEM_OWNER_ONLY";
export const DOSSIER_READ_ONLY_EVENT = "generex:dossier-read-only";

/** Refus d'une écriture par les droits du compte sur le dossier (lecture seule, action de propriétaire). */
export function accessRefused(e: unknown): e is ApiError {
  return e instanceof ApiError && (e.code === READ_ONLY || e.code === OWNER_ONLY);
}

/** Dossier ouvert (comptes nominatifs, docs/17 §3.4) : les sélecteurs de forces et d'ODB, les aperçus et les routes
 *  du terrain l'envoient (`base=`, `exercise=`), pour lire les références déjà enregistrées dans ce dossier lisible
 *  même quand elles sont privées à un autre compte. Posé par la coque (`sharing/DossierAccessHost`) pour un dossier
 *  de la liste ; null sans dossier ouvert. Sans effet en mode hérité. */
let openDossier: string | null = null;

export function setOpenDossier(id: string | null): void {
  openDossier = id;
}

/** Paramètre `name=<dossier ouvert>` à ajouter à une adresse (« » sans dossier ouvert). */
export function dossierParam(name: "base" | "exercise" | "dossier", prefix: "?" | "&" = "?"): string {
  return openDossier ? `${prefix}${name}=${encodeURIComponent(openDossier)}` : "";
}

/** Dossier enregistré affiché, quel que soit le mode (hérité compris) : sa création est le début d'un exercice sans
 *  date, comme l'OPO, pour les routes Météo, Mobilité et Analyse du terrain. Null pour un brouillon à créer. */
let shownDossier: string | null = null;

export function setShownDossier(id: string | null): void {
  shownDossier = id;
}

export function shownDossierId(): string | null {
  return shownDossier;
}

/** `?exercise=<dossier affiché>` des routes du terrain qui lisent la date de l'exercice (« » sans dossier). */
function clockParam(): string {
  return shownDossier ? `?exercise=${encodeURIComponent(shownDossier)}` : "";
}

async function apiError(res: Response): Promise<ApiError> {
  let message = res.statusText;
  let code: string | undefined;
  let extra: Record<string, unknown> | undefined;
  try {
    const body = (await res.json()) as { detail?: unknown };
    if (typeof body.detail === "string") message = body.detail;
    else if (body.detail && typeof body.detail === "object") {
      const detail = body.detail as { code?: unknown; message?: unknown };
      if (typeof detail.message === "string" && detail.message)
        message = detail.message;
      if (typeof detail.code === "string") code = detail.code;
      extra = body.detail as Record<string, unknown>;
    }
  } catch {
    /* corps non JSON */
  }
  // Licence suspendue par BELLICOM (administration des licences) : même relecture, l'écran « Licence suspendue » suit.
  if (res.status === 403 && (code === "LICENCE_REQUIRED" || code === "LICENCE_SUSPENDED"))
    window.dispatchEvent(new Event(LICENCE_REQUIRED_EVENT));
  if (res.status === 403 && code === LICENCE_READ_ONLY)
    window.dispatchEvent(new CustomEvent(LICENCE_READ_ONLY_EVENT, { detail: extra }));
  // Export refusé à une licence d'essai (registre F44) que le navigateur ne savait pas encore d'essai : l'état de la
  // licence est relu, les gestes d'export se grisent.
  if (res.status === 403 && code === "TRIAL_EXPORT") window.dispatchEvent(new Event(LICENCE_REQUIRED_EVENT));
  // Session de la personne : seulement pour une application montée pour un compte (les écrans de connexion traitent
  // eux-mêmes leurs refus).
  // Session exigée alors qu'aucun compte n'est monté (onglet ouvert en mode hérité avant l'ouverture des comptes) :
  // l'état de la licence est relu, le portail des comptes prend le relais.
  if (!mountedScope() && res.status === 401 && code === AUTH_REQUIRED && typeof window !== "undefined" && !res.url.includes("/api/auth/"))
    window.dispatchEvent(new Event(LICENCE_REQUIRED_EVENT));
  if (mountedScope() && typeof window !== "undefined") {
    // `sent_at` : départ de la requête, pour que le portail ignore le refus d'une requête partie avant la reprise.
    const detail = { ...(extra ?? {}), sent_at: sentAt.get(res) ?? 0 };
    if (res.status === 401 && code === AUTH_REQUIRED) window.dispatchEvent(new CustomEvent(AUTH_REQUIRED_EVENT, { detail }));
    if (res.status === 401 && code === SESSION_LOCKED) window.dispatchEvent(new CustomEvent(SESSION_LOCKED_EVENT, { detail }));
    if (res.status === 409 && code === ACCOUNT_MISMATCH) window.dispatchEvent(new CustomEvent(ACCOUNT_MISMATCH_EVENT, { detail }));
  }
  if (res.status === 403 && (code === READ_ONLY || code === OWNER_ONLY) && typeof window !== "undefined")
    window.dispatchEvent(new CustomEvent(DOSSIER_READ_ONLY_EVENT, { detail: { code, url: res.url } }));
  if (res.status === 413 && message.startsWith("Espace de stockage de la licence")) {
    code = STORAGE_FULL;
    window.dispatchEvent(new Event(STORAGE_FULL_EVENT));
  }
  if (res.status === 413 && code === ACCOUNT_QUOTA && typeof window !== "undefined")
    window.dispatchEvent(new CustomEvent(ACCOUNT_QUOTA_EVENT, { detail: { message: tServer(message) } }));
  const retry = res.status === 429 ? retryAfterMs(res.headers.get("Retry-After")) : null;
  return new ApiError(res.status, tServer(message), code, extra, retry ?? undefined, message);
}

/** Code d'un échec réseau (connexion coupée, serveur arrêté, relais injoignable) : la requête n'a reçu aucune
 *  réponse. Le message est traduit, au lieu du « Failed to fetch » brut du navigateur (audit de persistance m11). */
export const UNREACHABLE = "UNREACHABLE";

/** Fin de la fenêtre des requêtes non interruptibles ouverte par `sendingOnExit` (horodatage, ms). */
let exitingUntil = 0;
/** Durée de cette fenêtre : les écritures différées passent par des mutations react-query, dont l'appel réseau ne
 *  part qu'après des étapes asynchrones (`onMutate`, file du dossier) ; quelques tâches suffisent, une seconde
 *  les couvre largement sans faire passer en keepalive (quota de 64 Kio par page) les requêtes qui suivraient. */
export const EXIT_WINDOW_MS = 1000;
const exiting = () => Date.now() < exitingUntil;

/** Écritures différées envoyées à la fermeture de la page (curseur de potentiel, ⌘Z de la carte : audit de
 *  persistance m11) : les requêtes parties pendant `run`, ou à sa suite dans la seconde (mutation qui attend son
 *  tour), sont non interruptibles (keepalive). */
export function sendingOnExit(run: () => void): void {
  exitingUntil = Date.now() + EXIT_WINDOW_MS;
  run();
}

/** En-tête exigé par le serveur sur toute écriture (protection CSRF, docs/17 §4.7) : un autre site ne peut pas
 *  l'envoyer sans pré-vol CORS accepté. */
export const CSRF_HEADER = "X-Generex-CSRF";
/** Doublon du cookie du poste refusé par le serveur (docs/17 §4.4) : la réponse a effacé le cookie en trop. */
export const SEAT_COOKIE_CONFLICT = "SEAT_COOKIE_CONFLICT";

/** Requête d'écriture (toute méthode autre que GET et HEAD) augmentée de l'en-tête CSRF, quelle que soit la forme
 *  de ses en-têtes (objet, `Headers`, liste de paires) ; une lecture est rendue telle quelle. */
function withCsrf(init?: RequestInit): RequestInit | undefined {
  const method = (init?.method ?? "GET").toUpperCase();
  if (method === "GET" || method === "HEAD") return init;
  const headers = init?.headers;
  if (headers instanceof Headers) {
    const copy = new Headers(headers);
    copy.set(CSRF_HEADER, "1");
    return { ...init, headers: copy };
  }
  if (Array.isArray(headers)) return { ...init, headers: [...headers, [CSRF_HEADER, "1"]] };
  return { ...init, headers: { ...(headers ?? {}), [CSRF_HEADER]: "1" } };
}

/** Profondeur des relectures automatiques en cours d'appel (`background`) : lue de façon synchrone par `send()`,
 *  avant le premier `await`. */
let backgroundDepth = 0;

/** Relecture automatique (`refetchInterval` des vues : DIREX, situation, 3A, chaîne, licence, terrains…) : la requête
 *  partie pendant l'appel porte `X-Generex-Fond: 1`, mais seulement si la page n'est pas visible (docs/17 §4.5). Vue
 *  affichée, ses relectures comptent comme une activité : un observateur qui suit la DIREX n'est pas verrouillé par
 *  le serveur. `run` doit appeler l'API sans attendre au préalable (la requête part dans l'appel). */
export function background<T>(run: () => Promise<T>): Promise<T> {
  backgroundDepth++;
  try {
    return run();
  } finally {
    backgroundDepth--;
  }
}

/** Page visible (vrai hors navigateur : essais, rendu serveur). */
function pageVisible(): boolean {
  return typeof document === "undefined" || document.visibilityState === "visible";
}

/** Envois suspendus jusqu'au rechargement de la page (changement d'utilisateur, session ouverte pour un autre compte,
 *  docs/17 §4.8) : aucune requête de l'application montée pour A ne part plus, sauf celles que `allow` admet (la
 *  déconnexion). Une requête suspendue n'aboutit jamais : la page se recharge. */
let halted: ((url: string) => boolean) | null = null;

export function haltRequests(allow: (url: string) => boolean = () => false): void {
  halted = allow;
}

/** Essais seulement : reprise des envois. */
export function resumeRequestsForTests(): void {
  halted = null;
}

/** Heure de départ de chaque réponse reçue (refus de session : `sent_at`, docs/17 §4.8). */
const sentAt = new WeakMap<Response, number>();

/** Requêtes que charge la carte elle-même (tuiles, couches GeoJSON, sprites : `transformRequest` de mapbox-gl et
 *  maplibre-gl), qui ne passent pas par `send()` : même en-tête de compte vers l'API de même origine (§4.8). */
export function mapRequest(url: string): { url: string; headers?: Record<string, string> } {
  const scope = mountedScope();
  if (!scope || typeof window === "undefined") return { url };
  let path: string;
  try {
    const parsed = new URL(url, window.location.href);
    if (parsed.origin !== window.location.origin) return { url };
    path = parsed.pathname;
  } catch {
    return { url };
  }
  return path.startsWith("/api/") ? { url, headers: { [ACCOUNT_HEADER]: scope } } : { url };
}

/** En-têtes ajoutés à toute requête vers l'API : langue de l'interface (`X-Generex-Langue`), compte monté
 *  (`X-Generex-Compte`) et relecture de fond d'une page cachée (`X-Generex-Fond`). */
function withSession(input: string, init?: RequestInit): RequestInit | undefined {
  if (!input.startsWith("/api/")) return init;
  const extra: [string, string][] = [[LANGUAGE_HEADER, lang()]];
  const scope = mountedScope();
  if (scope) extra.push([ACCOUNT_HEADER, scope]);
  if (backgroundDepth > 0 && !pageVisible()) extra.push([BACKGROUND_HEADER, "1"]);
  if (!extra.length) return init;
  const headers = init?.headers;
  if (headers instanceof Headers) {
    const copy = new Headers(headers);
    for (const [name, value] of extra) copy.set(name, value);
    return { ...init, headers: copy };
  }
  if (Array.isArray(headers)) return { ...init, headers: [...headers, ...extra] };
  return { ...init, headers: { ...(headers ?? {}), ...Object.fromEntries(extra) } };
}

/** Refus 400 `SEAT_COOKIE_CONFLICT` : la garde s'arrête avant la route, rien n'a été fait. */
async function seatConflict(res: Response): Promise<boolean> {
  if (res.status !== 400) return false;
  try {
    const body = (await res.clone().json()) as { detail?: { code?: unknown } };
    return body.detail?.code === SEAT_COOKIE_CONFLICT;
  } catch {
    return false;
  }
}

/** `fetch` dont l'échec réseau devient une `ApiError` (statut 0, code `UNREACHABLE`, message traduit) ; une
 *  requête interrompue volontairement (AbortController) reste une interruption. Toute écriture porte l'en-tête CSRF
 *  (keepalive, envois de fichiers et flux compris). Doublon du cookie du poste (cookie déposé par un sous-domaine
 *  voisin) : la réponse l'a effacé, la requête est rejouée une fois, sans rien montrer. */
async function send(input: string, init?: RequestInit): Promise<Response> {
  // Page qui se ferme (`sendingOnExit`) : la requête survit à la fermeture (keepalive, corps de 64 Kio au plus).
  if (exiting() && !init?.keepalive && (typeof init?.body !== "string" || init.body.length < 60_000)) init = { ...init, keepalive: true };
  if (halted && !halted(input)) return new Promise<Response>(() => undefined);
  init = withSession(input, withCsrf(init));
  const started = Date.now();
  try {
    const first = await fetch(input, init);
    const res = (await seatConflict(first)) ? await fetch(input, init) : first;
    if (res && typeof res === "object") sentAt.set(res, started);
    return res;
  } catch (err) {
    if (init?.signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) throw err;
    throw new ApiError(0, t("Serveur injoignable : vérifiez la connexion du poste, puis réessayez."), UNREACHABLE);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await send(`/api${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...iaHeaders(), ...(init?.headers ?? {}) },
  });
  if (!res.ok) throw await apiError(res);
  return (res.status === 204 ? undefined : await res.json()) as T;
}

export interface AssistantEvent {
  event: "step" | "doctrine" | "route" | "token" | "source" | "done" | "error";
  data: {
    id?: string;
    label?: string;
    state?: "running" | "done" | "error";
    text?: string;
    message?: string;
    elapsed_s?: number;
    /** Sources de doctrine retenues (« Glossaire · LIMA »). */
    sources?: string[];
  } & Partial<AssistantRoute>;
}

/** Itinéraire pondéré par la menace tracé par l'assistant ([lon, lat]) : suites de tronçons de même exposition
 *  (0 aucune, 1 exposé, 2 à plus de la moitié de la menace maximale), départ et arrivée, emprise. */
export interface AssistantRoute {
  runs: { level: 0 | 1 | 2; coords: [number, number][] }[];
  origin: { name: string; lon: number; lat: number };
  destination: { name: string; lon: number; lat: number };
  bounds: [[number, number], [number, number]];
  length_m: number;
  exposed_m: number;
  avoid_eni: boolean;
  phase: "T1" | "T2" | "T3";
}

/** Assistant IA : réponse diffusée en Server-Sent Events (POST, donc lecture manuelle du flux). */
export async function streamAssistant(
  body: {
    question: string;
    exercise_id?: string | null;
    map_slug?: string | null;
    /** Temps affiché sur la carte : positions et missions des unités à ce temps. */
    phase?: "T1" | "T2" | "T3";
    /** Langue de la réponse : celle de l'interface (consigne, contexte et doctrine en anglais, comme l'iPad). */
    language?: "fr" | "en";
  },
  onEvent: (e: AssistantEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await send("/api/ai/assistant", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...iaHeaders(),
    },
    body: JSON.stringify(body),
    signal,
  });
  await readEvents(res, (event, data) =>
    onEvent({ event: event as AssistantEvent["event"], data: data as AssistantEvent["data"] }),
  );
}

/** Lecture d'une réponse en Server-Sent Events (requête POST : pas d'EventSource). */
async function readEvents(res: Response, onEvent: (event: string, data: unknown) => void): Promise<void> {
  if (!res.ok) throw await apiError(res);
  if (!res.body) throw new ApiError(res.status, res.statusText);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep: number;
    while ((sep = buffer.indexOf("\n\n")) >= 0) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      let event = "message";
      let data = "";
      for (const line of block.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).trim();
      }
      if (data) onEvent(event, JSON.parse(data));
    }
  }
}

/** Dossier d'exercice : rédaction diffusée (gabarits, puis préambule et situation géopolitique par l'IA). */
export async function streamBriefing(
  exerciseId: string,
  body: { sections?: string[] | null; keep_edits?: boolean; resume?: boolean },
  onEvent: (e: BriefingEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await send(`/api/exercises/${exerciseId}/briefing/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream", ...iaHeaders() },
    body: JSON.stringify(body),
    signal,
  });
  await readEvents(res, (event, data) =>
    onEvent({ event: event as BriefingEvent["event"], data: data as BriefingEvent["data"] }),
  );
}

/** Fiche pays : la fiche composée par Generex (`sheet`), puis ses rubriques de texte rédigées par l'IA, diffusées. */
export async function streamCountrySheet(
  exerciseId: string,
  body: { sections?: string[] | null; keep_edits?: boolean; resume?: boolean; reseed?: boolean; reuse?: boolean },
  onEvent: (e: CountrySheetEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await send(`/api/exercises/${exerciseId}/country-sheet/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream", ...iaHeaders() },
    body: JSON.stringify(body),
    signal,
  });
  await readEvents(res, (event, data) =>
    onEvent({ event: event as CountrySheetEvent["event"], data: data as CountrySheetEvent["data"] }),
  );
}

/** Wargame MANFUT : simulation calculée puis évaluation du modèle, diffusées en Server-Sent Events. */
export async function streamWargame(
  exerciseId: string,
  me: 1 | 2,
  opo: number | undefined,
  onEvent: (e: WargameEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const query = opo && opo !== 1 ? `?opo=${opo}` : "";
  const res = await send(`/api/exercises/${exerciseId}/coa/wargame${query}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream", ...iaHeaders() },
    body: JSON.stringify({ me }),
    signal,
  });
  await readEvents(res, (event, data) =>
    onEvent({ event: event as WargameEvent["event"], data: data as WargameEvent["data"] }),
  );
}

/** Aide rédactionnelle 3A d'une unité, rédigée au fil de l'eau par le modèle local. */
export async function streamAarText(
  exerciseId: string,
  target: string,
  opo: number | undefined,
  onEvent: (e: AarTextEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const query = new URLSearchParams({ target });
  if (opo && opo !== 1) query.set("opo", String(opo));
  const res = await send(`/api/exercises/${exerciseId}/aar/text?${query}`, {
    method: "POST",
    headers: { Accept: "text/event-stream", ...iaHeaders() },
    signal,
  });
  await readEvents(res, (event, data) =>
    onEvent({ event: event as AarTextEvent["event"], data: data as AarTextEvent["data"] }),
  );
}

/** Suggestions du modèle local pour un ordre reçu (synthèse, intention, tâches implicites, contraintes). */
export async function streamReceivedSuggestions(
  exerciseId: string,
  orderId: string,
  onEvent: (e: ReceivedSuggestionEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await send(`/api/exercises/${exerciseId}/received-orders/${orderId}/suggestions`, {
    method: "POST",
    headers: { Accept: "text/event-stream", ...iaHeaders() },
    signal,
  });
  await readEvents(res, (event, data) =>
    onEvent({ event: event as ReceivedSuggestionEvent["event"], data: data as ReceivedSuggestionEvent["data"] }),
  );
}

/** « Proposer avec l'IA » d'une vérification d'OPO : rubriques douteuses ou absentes demandées au modèle local. */
export async function streamOpoProposals(
  draftId: string,
  onEvent: (e: OpoProposalEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await send(`/api/exercises/import-opo/drafts/${draftId}/proposals?${new URLSearchParams({ lang: lang() })}`, {
    method: "POST",
    headers: { Accept: "text/event-stream", ...iaHeaders() },
    signal,
  });
  await readEvents(res, (event, data) =>
    onEvent({ event: event as OpoProposalEvent["event"], data: data as OpoProposalEvent["data"] }),
  );
}

/** Paramètre d'OPO (CENTAC) ; les autres niveaux l'ignorent. */
function withOpo(
  path: string,
  opo: number | undefined,
  query = new URLSearchParams(),
): string {
  if (opo && opo !== 1) query.set("opo", String(opo));
  const qs = query.toString();
  return qs ? `${path}?${qs}` : path;
}

/** Dossier reçu, ses avis annoncés à la carte (`ExerciseOut.notices`). */
function announced(ex: Exercise): Exercise {
  emitServerNotices(ex.notices);
  return ex;
}

/** Choix d'une mission (`PUT /exercises/{id}/missions`). */
export interface MissionChoice {
  /** Clé de l'unité (champ `key` du déploiement). */
  unit: string;
  phase: Phase;
  /** Code du catalogue de l'échelon ; « » : sans mission ; null : mission doctrinale (selon le rôle). */
  mission: string | null;
  /** Placer l'unité selon la mission (ancrage) ; sinon sa position est gardée. */
  reposition: boolean;
}

/** Clés de la manœuvre libre, des lignes de la ZO, des rôles choisis et de la composition du dossier dans
 *  `PUT /placement` : absentes = conservées, null = supprimées. */
export const MANOEUVRE_KEYS = [
  "corridor",
  "missions",
  "anchored",
  "measures",
  "eni_roles",
  "orientation",
  "composition",
  "ami_roles",
  "posture",
] as const;

/** Réponse de `PATCH /exercises/{id}` rendue comme un dossier de la liste (docs/18 §5.3) : la recomposition des OPO
 *  (`orders: {updated, finalized}`) et les paragraphes à revoir passent dans `refreshed`, pour ne jamais remplacer
 *  le décompte des ordres de la liste des dossiers (`orders: {count, finalized}`), qui porte le même nom. */
export function savedExercise(raw: ExerciseSaved): Exercise {
  const { orders, outdated, ...rest } = raw;
  const exercise: Exercise = { ...rest };
  if (orders && "count" in orders) exercise.orders = orders;
  const refreshedOrders = orders && "updated" in orders ? orders : null;
  if (refreshedOrders || outdated) exercise.refreshed = { orders: refreshedOrders, outdated: outdated ?? null };
  return exercise;
}

/** Dossiers recomposés après l'enregistrement (ou la suppression forcée) d'un ordre de bataille : le serveur range
 *  les paragraphes à revoir dans le relevé des ordres (`orders.outdated`), remontés au niveau du dossier comme dans
 *  les autres réponses (`OrdersRefresh.outdated`). */
function odbDossiers(raw: OdbRefreshedDossier[] | null | undefined): OdbRefreshedDossier[] | undefined {
  if (!raw) return undefined;
  return raw.map((d) => {
    const { outdated, ...orders } = (d.orders ?? {}) as OrdersRefreshed & { outdated?: Record<string, string[]> | null };
    return { ...d, orders: d.orders ? orders : null, outdated: d.outdated ?? outdated ?? null };
  });
}

/** Écriture d'une ligne de la ZO (`PUT /measures`, docs/15 §3.7) : `ends` [sG, sD] = position choisie (au temps
 *  `phase` pour la FLOT et la CFL), `ends: null` = position calculée, `ends` absent (`undefined`) = position
 *  inchangée ; `off` : retirer (true) ou rétablir (false) la CFL ou la FSCL. */
export interface MeasureChange {
  key: MeasureKey;
  phase?: Phase | null;
  ends?: MeasureEnds | null;
  off?: boolean | null;
}

/** Corps de `PUT /measures` : `ends` présent seulement s'il est défini (null compris : « position calculée ») — le
 *  serveur distingue le champ absent du champ nul ; `off` absent s'il n'est pas défini. */
export function measureBody(change: MeasureChange): Record<string, unknown> {
  const body: Record<string, unknown> = { key: change.key, phase: change.phase ?? null };
  if (change.ends !== undefined) body.ends = change.ends === null ? null : [change.ends[0], change.ends[1]];
  if (change.off !== undefined && change.off !== null) body.off = change.off;
  return body;
}

/** Fuseau tel qu'il s'envoie (`CorridorIn`) : sans `seed` ni `prior_lines`, calculés par le serveur. */
export function corridorBody(corridor: CorridorModel): CorridorModel {
  const { seed: _seed, prior_lines: _prior, ...body } = corridor;
  return body;
}

/** Corps de `PUT /placement` : positions, temps des rôles, glissements de LIMA et positions CENTAC toujours
 *  présents ; `corridor`, `missions`, `anchored` et `measures` présents seulement s'ils sont définis (null
 *  compris : « supprimé »), absents s'ils valent `undefined` (« conservé ») ; ponts détruits, graphiques et
 *  potentiel jamais envoyés (le serveur les garde, ils ont leurs propres routes). */
export function placementBody(state: Placement): Record<string, unknown> {
  const body: Record<string, unknown> = {
    positions: state.positions ?? {},
    roles_phase: state.roles_phase ?? null,
    lines: state.lines ?? {},
    opo_positions: state.opo_positions ?? {},
  };
  for (const key of MANOEUVRE_KEYS) {
    const value = state[key];
    if (value === undefined) continue;
    body[key] = key === "corridor" && value ? corridorBody(value as CorridorModel) : value;
  }
  return body;
}

/** Nom du fichier annoncé par le serveur (« filename* » UTF-8, sinon « filename »). */
function attachmentName(header: string | null): string | null {
  if (!header) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header)?.[1];
  if (utf8) {
    try {
      return decodeURIComponent(utf8);
    } catch {
      /* nom mal encodé : repli sur « filename » */
    }
  }
  return /filename="([^"]+)"/i.exec(header)?.[1] ?? null;
}

/** Export de la situation (POST : les images des symboles voyagent dans le corps) : le fichier est enregistré
 *  par le navigateur sous le nom donné par le serveur, renvoyé. */
export async function downloadSituation(
  exerciseId: string,
  fmt: SituationFormat,
  body: SituationExportRequest,
  opo?: number,
): Promise<string> {
  const res = await send(`/api${withOpo(`/exercises/${exerciseId}/exports/${fmt}`, opo)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await apiError(res);
  const name = attachmentName(res.headers.get("Content-Disposition")) ?? `Generex.${fmt}`;
  saveFile(await res.blob(), name);
  return name;
}

/** Enregistrement d'un fichier reçu sous le nom `name` (lien de téléchargement éphémère). */
function saveFile(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/** Code d'une réponse reçue qui n'est pas le fichier attendu (vide, d'un autre type, sans nom) : rien n'est
 *  enregistré. */
export const NOT_A_FILE = "NOT_A_FILE";

/** Téléchargement d'un PDF produit par l'API (dossier complet, fiche pays, mise en ambiance, OPO ; constat
 *  d'Alexis du 14/10/2026 : un lien direct enregistrait un fichier « pdf » vide quand le relais répondait sans
 *  corps). Requête de l'application (cookies, compte monté, comme toute lecture) ; le fichier n'est enregistré,
 *  sous le nom donné par le serveur, que s'il est bien un PDF non vide. Sinon `ApiError` : refus du serveur
 *  (son code et son détail, `DOSSIER_INCOMPLETE` compris), serveur injoignable (`UNREACHABLE`), réponse sans
 *  fichier (`NOT_A_FILE`). Renvoie le nom du fichier enregistré. */
/** Étape de la genèse du dossier complet, dans l'ordre de calcul du serveur (`dossier_jobs.STEPS`). */
export type DossierStepId = "country" | "briefing" | "oplan" | "order" | "odb_ami" | "odb_eni" | "map" | "cover" | "merge";

/** Genèse du dossier complet publiée par le serveur (registre F37) : `progress` de 0 à 1 (1 seulement une fois le
 *  fichier produit), `remaining_s` connu seulement quand des genèses du même niveau ont été mesurées. */
export interface DossierJob {
  id: string;
  state: "queued" | "running" | "done" | "failed" | "interrupted";
  number: number;
  steps: { id: DossierStepId; status: "pending" | "running" | "done" | "skipped" }[];
  progress: number;
  elapsed_s: number;
  remaining_s: number | null;
  filename: string | null;
  size: number | null;
  error: string | null;
  downloaded: boolean;
}

/** Document lu dans l'autre langue dont une traduction est en cours (409 `TRANSLATING`, docs/13) : le
 *  téléchargement réessaie de lui-même (la carte de préparation reste en attente animée), au plus
 *  `TRANSLATION_WAIT_MS`. */
export const TRANSLATING = "TRANSLATING";
export const TRANSLATION_POLL_MS = 2500;
export const TRANSLATION_WAIT_MS = 5 * 60_000;
export const downloadDeps = { sleep: (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)), now: () => Date.now() };

async function translating(res: Response): Promise<boolean> {
  if (res.status !== 409) return false;
  try {
    const body = (await res.clone().json()) as { detail?: { code?: unknown } };
    return body.detail?.code === TRANSLATING;
  } catch {
    return false;
  }
}

export async function downloadPdf(path: string): Promise<string> {
  const started = downloadDeps.now();
  let res = await send(path);
  while ((await translating(res)) && downloadDeps.now() - started < TRANSLATION_WAIT_MS) {
    await downloadDeps.sleep(TRANSLATION_POLL_MS);
    res = await send(path);
  }
  if (!res.ok) {
    const error = await apiError(res);
    // Relais sans réponse de l'API (502, 503, 504 : arrêt, redémarrage, délai) : message clair plutôt que
    // « Bad Gateway ».
    if (res.status >= 502 && res.status <= 504 && !error.code)
      throw new ApiError(res.status, t("Le serveur n'a pas pu préparer le fichier. Réessayez dans un instant."), NOT_A_FILE);
    throw error;
  }
  const name = attachmentName(res.headers.get("Content-Disposition"));
  const type = res.headers.get("Content-Type") ?? "";
  const bytes = new Uint8Array(await res.arrayBuffer());
  const pdf = bytes.length > 5 && String.fromCharCode(...bytes.subarray(0, 5)) === "%PDF-";
  if (!name || !type.includes("pdf") || !pdf)
    throw new ApiError(res.status, t("Le serveur a renvoyé un fichier vide ou illisible. Réessayez dans un instant."), NOT_A_FILE);
  saveFile(new Blob([bytes], { type: "application/pdf" }), name);
  return name;
}

/** Liste d'une réponse : le tableau lui-même, ou le tableau rangé sous `key`. */
function listOf<T>(response: unknown, key: string): T[] {
  if (Array.isArray(response)) return response as T[];
  const inner = response && typeof response === "object" ? (response as Record<string, unknown>)[key] : null;
  return Array.isArray(inner) ? (inner as T[]) : [];
}

export const api = {
  licence: () => request<LicenceStatus>("/licence"),
  /** `admin` : identifiants d'un administrateur de l'unité, exigés pour reprendre une place sur une licence en mode
   *  comptes (docs/17 §5.7) ; envoyés dans le corps seulement. */
  activateLicence: (code: string, deviceName?: string, replace = false, admin?: { login: string; password: string }) =>
    request<LicenceStatus>("/licence/activate", {
      method: "POST",
      body: JSON.stringify({
        code,
        device_name: deviceName?.trim() || null,
        replace,
        ...(admin ? { admin_login: admin.login, admin_password: admin.password } : {}),
      }),
    }),
  /** Postes web de la licence de ce poste (registre F25). */
  licenceTerminals: () => request<LicenceTerminal[]>("/licence/terminals"),
  releaseLicenceTerminal: (ref: string) =>
    request<LicenceTerminal[]>(`/licence/terminals/${encodeURIComponent(ref)}/release`, { method: "POST" }),
  checkLicence: () =>
    request<LicenceStatus>("/licence/check", { method: "POST" }),
  acceptPrivacy: () =>
    request<LicenceStatus>("/licence/privacy", {
      method: "POST",
      body: JSON.stringify({ accepted: true }),
    }),
  deactivateLicence: () =>
    request<LicenceStatus>("/licence/deactivate", { method: "POST" }),
  /** « Qui utilise ce poste ? » : nom et prénom obligatoires, grade et service facultatifs. */
  declareIdentity: (identity: { last_name: string; first_name: string; grade?: string | null; service?: string | null }) =>
    request<LicenceStatus>("/licence/identity", { method: "PUT", body: JSON.stringify(identity) }),
  /** Usage de l'interface (vues ouvertes, temps passé), par lots : jamais le contenu d'un dossier. */
  postUsage: (batch: { items: { view: string; opens: number; seconds: number }[]; current: string | null }) =>
    request<{ credited: number }>("/usage", { method: "POST", body: JSON.stringify(batch) }),

  /** Comptes nominatifs (docs/17 §7.4) : session de ce navigateur, connexion, codes, verrouillage, mot de passe,
   *  notice, sessions et événements de la personne. Les secrets ne vont que dans le corps des requêtes. */
  auth: {
    session: () => request<AuthSession>("/auth/session"),
    bootstrap: (body: {
      bootstrap_code: string;
      unit_name: string | null;
      display_name: string;
      appointment: string | null;
      login: string;
      password: string;
    }) => request<AuthSession>("/auth/bootstrap", { method: "POST", body: JSON.stringify(body) }),
    login: (login: string, password: string, expectAccount?: string | null) =>
      request<AuthSession>("/auth/login", {
        method: "POST",
        body: JSON.stringify(expectAccount ? { login, password, expect_account: expectAccount } : { login, password }),
      }),
    redeem: (login: string, code: string, newPassword: string) =>
      request<AuthSession>("/auth/redeem", { method: "POST", body: JSON.stringify({ login, code, new_password: newPassword }) }),
    recover: (login: string, recoveryCode: string, newPassword: string) =>
      request<AuthSession>("/auth/recover", {
        method: "POST",
        body: JSON.stringify({ login, recovery_code: recoveryCode, new_password: newPassword }),
      }),
    /** Déverrouillage ; session déjà reprise sous un nouveau jeton par un autre onglet (409 `SESSION_RESUMED`, deux
     *  envois simultanés) : la session est relue, avec le cookie que l'autre réponse a déposé. */
    unlock: (password: string) =>
      request<AuthSession>("/auth/unlock", { method: "POST", body: JSON.stringify({ password }) }).catch((error: unknown) => {
        if (error instanceof ApiError && error.code === "SESSION_RESUMED") return request<AuthSession>("/auth/session");
        throw error;
      }),
    lock: () => request<unknown>("/auth/lock", { method: "POST" }),
    logout: () => request<unknown>("/auth/logout", { method: "POST" }),
    password: (current: string, next: string) =>
      request<unknown>("/auth/password", {
        method: "POST",
        body: JSON.stringify({ current_password: current, new_password: next }),
      }),
    notice: () => request<AuthSession>("/auth/notice", { method: "POST", body: JSON.stringify({ accepted: true }) }),
    // Liste rendue telle quelle, ou dans un objet (`{sessions: […]}`, `{events: […]}`).
    sessions: () => request<unknown>("/auth/sessions").then((r) => listOf<AuthSessionRow>(r, "sessions")),
    endSession: (ref: string) => request<unknown>(`/auth/sessions/${encodeURIComponent(ref)}`, { method: "DELETE" }),
    events: () => request<unknown>("/auth/events").then((r) => listOf<AuthEvent>(r, "events")),
    reauth: (password: string) => request<AuthSession>("/auth/reauth", { method: "POST", body: JSON.stringify({ password }) }),
  },

  /** Comptes de l'unité (docs/17 §5, §7.4, lot 4) : administrateurs seulement, sauf l'annuaire. Toute écriture peut
   *  recevoir 403 `REAUTH_REQUIRED` (mot de passe à confirmer) ; une demande à deux administrateurs répond 202. */
  accounts: {
    directory: () => request<{ id: string; display_name: string; appointment: string | null }[]>("/accounts/directory"),
    list: () => request<AccountsList>("/accounts"),
    create: (body: { display_name: string; appointment: string | null; login: string | null; admin: boolean }) =>
      request<CreatedAccount>("/accounts", { method: "POST", body: JSON.stringify(body) }),
    batchCheck: (text: string) => request<BatchPreview>("/accounts/batch", { method: "POST", body: JSON.stringify({ text, check: true }) }),
    batch: (text: string) => request<{ created: IssuedCode[] }>("/accounts/batch", { method: "POST", body: JSON.stringify({ text }) }),
    update: (id: string, body: { version: number; display_name?: string; appointment?: string | null; role?: "admin" | "member" }) =>
      request<{ account: UnitAccount } | PendingFiled>(`/accounts/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    items: (id: string) => request<{ items: AccountItem[] }>(`/accounts/${encodeURIComponent(id)}/items`),
    /** Gestes d'une ligne : code rendu une fois, demande déposée (202) ou compte mis à jour. */
    act: (id: string, gesture: "reset-code" | "activation-code" | "lock" | "unlock" | "clear-delay" | "end-sessions" | "reactivate") =>
      request<IssuedCode | PendingFiled | { account: UnitAccount }>(`/accounts/${encodeURIComponent(id)}/${gesture}`, { method: "POST" }),
    deactivate: (id: string, transferTo: string | null) =>
      request<{ account: UnitAccount } | PendingFiled>(`/accounts/${encodeURIComponent(id)}/deactivate`, {
        method: "POST",
        body: JSON.stringify({ transfer_to: transferTo }),
      }),
    /** Éléments d'un compte déjà désactivé passés au compte choisi (l'administrateur par défaut). */
    transfer: (id: string, transferTo: string | null) =>
      request<{ account: UnitAccount }>(`/accounts/${encodeURIComponent(id)}/transfer`, {
        method: "POST",
        body: JSON.stringify({ transfer_to: transferTo }),
      }),
  },

  /** Administration de l'unité (docs/17 §5.4, §5.7, §5.8, lot 4). */
  unit: {
    requests: () => request<AdminRequestRow[]>("/unit/requests"),
    approve: (id: string) => request<Approval>(`/unit/requests/${encodeURIComponent(id)}/approve`, { method: "POST" }),
    decline: (id: string) => request<{ id: string; status: string }>(`/unit/requests/${encodeURIComponent(id)}`, { method: "DELETE" }),
    sessions: () => request<UnitSessionsView>("/unit/sessions"),
    endSession: (ref: string) => request<unknown>(`/unit/sessions/${encodeURIComponent(ref)}`, { method: "DELETE" }),
    revokeAll: () => request<{ ended: number }>("/unit/sessions/revoke-all", { method: "POST" }),
    clearSeat: (ref: string) => request<unknown>(`/unit/seats/${encodeURIComponent(ref)}/clear-throttle`, { method: "POST" }),
    clearReplace: () => request<unknown>("/unit/throttle/replace-space", { method: "DELETE" }),
    audit: (filters: AuditFilters) => {
      const query = new URLSearchParams(Object.entries(filters).filter((entry): entry is [string, string] => Boolean(entry[1])));
      return request<UnitAuditView>(`/unit/audit${query.size ? `?${query.toString()}` : ""}`);
    },
    settings: () => request<UnitSettingsView>("/unit/settings"),
    saveSettings: (body: Partial<Pick<UnitSettingsView, "unit_name" | "lock_minutes" | "default_share" | "account_quota_bytes">>) =>
      request<UnitSettingsView>("/unit/settings", { method: "PUT", body: JSON.stringify(body) }),
    regenerateRecovery: () => request<{ code: string }>("/unit/recovery-code", { method: "POST" }),
  },

  /** Partage et propriété d'un dossier, éléments de l'unité (docs/17 §3, §5.6, lot 5) : en mode comptes seulement. */
  sharing: {
    access: (exerciseId: string) => request<ExerciseAccessView>(`/exercises/${exerciseId}/access`),
    save: (exerciseId: string, body: { unit: UnitShare; members: { account_id: string; level: "reader" | "editor" }[] }) =>
      request<ExerciseAccessView>(`/exercises/${exerciseId}/access`, { method: "PUT", body: JSON.stringify(body) }),
    transfer: (exerciseId: string, accountId: string) =>
      request<ExerciseAccessView>(`/exercises/${exerciseId}/owner`, { method: "POST", body: JSON.stringify({ account_id: accountId }) }),
    unitItems: () => request<UnitItems>("/unit/items"),
    assign: (body: { account_id: string; exercises?: string[]; odbs?: string[]; zones?: string[] }) =>
      request<UnitItems & { assigned: { exercises: number; odbs: number; zones: number } }>("/unit/assign", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  /** Préproduction (registre F35) : résumé de la promotion, demande, suivi. Routes montées par l'API de
   *  préproduction seulement (404 en production) ; le client ne les appelle que si l'API annonce `preprod`. Le code
   *  de validation ne va que dans le corps de la demande. */
  preprod: {
    promotion: () => request<PromotionSummary>("/preprod/promotion"),
    promote: (body: PromotionRequest) =>
      request<{ request_id: string; status: PromotionStatus }>("/preprod/promotion", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    promotionStatus: (requestId: string) =>
      background(() =>
        request<PromotionStatusReply>(`/preprod/promotion/status?request_id=${encodeURIComponent(requestId)}`),
      ),
    /** Annonce d'une maintenance de la production (registre F40) : même mot de passe que la promotion. */
    announcement: () => request<AnnouncementSummary>("/preprod/announcement"),
    announce: (body: AnnouncementRequest) =>
      request<{ request_id: string; status: AnnouncementStatus }>("/preprod/announcement", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    announcementStatus: (requestId: string) =>
      background(() =>
        request<{ status: AnnouncementStatus | null; stalled: boolean }>(
          `/preprod/announcement/status?request_id=${encodeURIComponent(requestId)}`,
        ),
      ),
    /** Sauvegarde complète de Generex-Web (registre F43) : même mot de passe que la promotion ; le jeton rendu ne
     *  sert qu'au téléchargement (présenté une fois, gardé ensuite par un cookie du chemin du téléchargement). */
    backup: () => request<BackupSummary>("/preprod/backup"),
    backupRequest: (body: BackupRequest) =>
      request<{ request_id: string; token: string; status: BackupStatus }>("/preprod/backup", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    backupStatus: (requestId: string) =>
      background(() =>
        request<{ status: BackupStatus | null; stalled: boolean }>(
          `/preprod/backup/status?request_id=${encodeURIComponent(requestId)}`,
        ),
      ),
    backupTicket: (body: { request_id: string; token: string }) =>
      request<{ url: string; name: string; size: number }>("/preprod/backup/ticket", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  /** Retours des utilisateurs (registre F39) : envoi, « Mes retours », réponses lues ; administration (licences
   *  désignées par le serveur seulement, 404 ailleurs). */
  feedback: {
    mine: () => request<FeedbackMine>("/feedback"),
    send: (body: FeedbackRequest) => request<FeedbackItem>("/feedback", { method: "POST", body: JSON.stringify(body) }),
    seen: () => request<{ seen: number }>("/feedback/seen", { method: "POST" }),
    screenshotUrl: (id: string) => `/api/feedback/${id}/screenshot`,
    adminList: (filters: FeedbackAdminFilters = {}) => {
      const query = new URLSearchParams();
      for (const [key, value] of Object.entries(filters)) if (value) query.set(key, value);
      const suffix = query.toString();
      return request<FeedbackAdminList>(`/feedback/admin${suffix ? `?${suffix}` : ""}`);
    },
    adminUpdate: (id: string, body: { status?: FeedbackStatus; reply?: string }) =>
      request<FeedbackItem>(`/feedback/admin/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    adminScreenshotUrl: (id: string) => `/api/feedback/admin/${id}/screenshot`,
  },

  aiStatus: () =>
    request<{
      available: boolean;
      source: string;
      models: string[];
      detail: string;
      /** GPU en échec et IA de secours en démarrage (veilleur du serveur, registre E29) : reprise attendue. */
      resuming?: boolean;
      /** Serveur GPU en panne : « Avancé » indisponible (les deux choix passent par l'IA de secours). */
      gpu_down?: boolean;
      /** Modèle choisi par ce poste et modèles proposés par le serveur (registre E26). */
      choice?: IaModel;
      choices?: IaModel[];
    }>("/ai/status"),
  maps: () => request<MapSummary[]>("/maps"),

  // Zones libres de la licence (manœuvre libre, étape 2 ; docs/14 §4.3) et espace de stockage.
  zones: () => request<Zone[]>("/zones"),
  zone: (id: string) => request<Zone>(`/zones/${id}`),
  /** Crée la zone (mise en file) à partir de l'axe tracé ; avec un dossier, le rattache (version + 1). */
  createZone: (body: { name: string; axis: ZoneAxis; exercise?: Pick<Exercise, "id" | "version"> }) =>
    request<ZoneCreated>("/zones", {
      method: "POST",
      body: JSON.stringify({ name: body.name, axis: body.axis, exercise_id: body.exercise?.id }),
      headers: body.exercise ? { "If-Match": `"${body.exercise.version}"` } : undefined,
    }),
  /** Recrée la zone dont la description est gardée dans le dossier (import ou réception refusés par la file). */
  recreateZone: (ex: Pick<Exercise, "id" | "version">) =>
    request<ZoneCreated>("/zones/recreate", {
      method: "POST",
      body: JSON.stringify({ exercise_id: ex.id }),
      headers: { "If-Match": `"${ex.version}"` },
    }),
  /** Renomme la zone ou change son climat (l'un des sept, ou « auto » : climat par défaut). */
  updateZone: (id: string, patch: { name?: string; climate?: string }) =>
    request<Zone>(`/zones/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  extendZone: (id: string, bbox: BBox) =>
    request<Zone>(`/zones/${id}/extend`, { method: "POST", body: JSON.stringify({ bbox }) }),
  retryZone: (id: string) => request<Zone>(`/zones/${id}/retry`, { method: "POST" }),
  cancelZone: (id: string) => request<Zone>(`/zones/${id}/cancel`, { method: "POST" }),
  /** Supprime la zone ; 409 `ZONE_IN_USE` (dossiers et transmissions qui la citent) sauf `force`. */
  deleteZone: (id: string, force = false) =>
    request<ZoneDeleted>(`/zones/${id}${force ? "?force=true" : ""}`, { method: "DELETE" }),
  storage: () => request<StorageUsage>("/storage"),
  /** Numéro de version et nouveautés des dernières versions (bas de la barre latérale). */
  releaseNotes: () => request<ReleaseNotes>("/release-notes"),

  /** Terrain lu dans le dossier ouvert (`exercise=`) : celui d'un dossier partagé reste lisible même privé. */
  map: (slug: string) => request<MapDetail>(`/maps/${slug}${dossierParam("exercise")}`),
  layer: (slug: string, layer: LayerKey) =>
    request<FeatureCollection>(`/maps/${slug}/layers/${layer}${dossierParam("exercise")}`),
  /** « Visible par toute l'unité » ou « Privé » d'un terrain libre (propriétaire et administrateurs). */
  zoneVisibility: (id: string, visibility: "unit" | "private") =>
    request<Zone>(`/zones/${id}/visibility`, { method: "PUT", body: JSON.stringify({ visibility }) }),

  exercises: () => request<Exercise[]>("/exercises"),
  // Avis du serveur (météo choisie ramenée au terrain, CENTAC…) : annoncés à la carte, qui les joint à « Dossier
  // créé ».
  createExercise: (body: {
    name: string;
    language?: "fr" | "en";
    map_slug?: string | null;
    parameters?: Partial<ExerciseParameters>;
  }) =>
    request<Exercise>("/exercises", {
      method: "POST",
      body: JSON.stringify(body),
    }).then(announced),
  /** Dossier d'exemple de la prise en main : créé comme `createExercise`, puis, sur un camp, fuseau retouché et
   *  mission choisie par le serveur (docs/14 §2.12). */
  createExampleExercise: (body: {
    name: string;
    language?: "fr" | "en";
    map_slug?: string | null;
    parameters?: Partial<ExerciseParameters>;
  }) =>
    request<ExampleExercise>("/exercises/example", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateExercise: (
    ex: Pick<Exercise, "id" | "version">,
    patch: {
      name?: string;
      language?: "fr" | "en";
      map_slug?: string | null;
      parameters?: ExerciseParameters;
    },
  ) =>
    // Avis du serveur (fuseau personnalisé ou missions choisies effacés par le changement, §3.8) : annoncés à
    // la carte, qui les affiche.
    request<ExerciseSaved>(`/exercises/${ex.id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
      headers: { "If-Match": `"${ex.version}"` },
    })
      .then(savedExercise)
      .then(announced),
  /** Suppression sur la version affichée : un dossier modifié entre-temps sur un autre poste n'est pas supprimé
   *  (412, la liste se relit) ; sans version, le serveur refuse (428). */
  /** Purge de l'accueil : tous les dossiers de la licence et ses imports en cours, définitivement. */
  purgeExercises: () =>
    request<{ exercises: number; drafts: number }>("/exercises/purge", {
      method: "POST",
      body: JSON.stringify({ confirmation: "EFFACER" }),
    }),
  deleteExercise: (ex: Pick<Exercise, "id" | "version">) =>
    request<void>(`/exercises/${ex.id}`, { method: "DELETE", headers: { "If-Match": `"${ex.version}"` } })
      // Déjà supprimé (demande précédente, autre poste) : la suppression a eu lieu, jamais un échec (registre F38).
      .catch((error: unknown) => {
        if (!(error instanceof ApiError && error.status === 404)) throw error;
      })
      .then(() =>
        // Brouillons du dossier gardés sur ce poste (Données initiales, OPO, incidents, 3A…) : sans objet (m8).
        licenceScope.forgetDossier(ex.id),
      ),

  /** Forces du sélecteur ; dans le dossier ouvert (`base=`), celles de son ODB s'ajoutent (`from_exercise`). */
  forces: (level: Level, base: string | null = openDossier) =>
    request<Force[]>(`/forces?${new URLSearchParams({ level, ...(base ? { base } : {}) })}`),
  /** Ordres de bataille construits de la licence et catalogue, d'un camp (amis par défaut). `inDossier` : sélecteur
   *  d'un dossier ouvert, qui ajoute ses ODB enregistrés (`base=`). */
  odbs: (side: OdbSide = "AMI", base: string | null = null) => {
    const query = new URLSearchParams({ ...(side === "ENI" ? { side } : {}), ...(base ? { base } : {}) });
    return request<{ user: OdbSummary[]; catalog: OdbSummary[] }>(`/odbs${query.size ? `?${query.toString()}` : ""}`);
  },
  /** « Visible par toute l'unité » ou « Privé » d'un ordre de bataille construit (propriétaire et administrateurs). */
  odbVisibility: (code: string, visibility: "unit" | "private") =>
    request<OdbDetail>(`/odbs/${encodeURIComponent(code)}/visibility`, { method: "PUT", body: JSON.stringify({ visibility }) }),
  odbVocabulary: (side: OdbSide = "AMI") =>
    request<OdbVocabulary>(side === "ENI" ? "/odbs/vocabulary?side=ENI" : "/odbs/vocabulary"),
  /** Départ d'un ordre de bataille ennemi (rien n'est enregistré) : la seule racine d'un ennemi générique (sans
   *  faction), ou la partie de la division d'une faction du catalogue qui fait face au niveau choisi. */
  odbStarter: (body: Omit<OdbStarterIn, "side">, signal?: AbortSignal) =>
    request<OdbStarter>("/odbs/starter", { method: "POST", body: JSON.stringify({ side: "ENI", ...body }), signal }),
  odb: (code: string) => request<OdbDetail>(`/odbs/${encodeURIComponent(code)}`),
  previewOdb: (kind: OdbKind, tree: OdbNode, signal?: AbortSignal) =>
    request<OdbPreview>("/odbs/preview", { method: "POST", body: JSON.stringify({ kind, tree }), signal }),
  createOdb: (kind: OdbKind, tree: OdbNode) =>
    request<OdbDetail>("/odbs", { method: "POST", body: JSON.stringify({ kind, tree }) }),
  /** `version` : celle de l'ordre de bataille lu ; 412 `ODB_CHANGED` si un autre poste l'a enregistré depuis. Le
   *  dossier ouvert (`dossier=`) voit ses OPO recomposés d'abord. */
  updateOdb: (code: string, kind: OdbKind, tree: OdbNode, version: number) =>
    request<OdbDetail>(`/odbs/${encodeURIComponent(code)}${dossierParam("dossier")}`, {
      method: "PUT",
      body: JSON.stringify({ kind, tree, version }),
    }).then((odb) => ({ ...odb, refreshed: odbDossiers(odb.refreshed) })),
  /** Suppression ; forcée, rend les dossiers qui l'employaient et dont les OPO ont été recomposés (rien : 204). */
  deleteOdb: (code: string, force = false) =>
    request<{ refreshed?: OdbRefreshedDossier[] } | undefined>(`/odbs/${encodeURIComponent(code)}?force=${force}${dossierParam("dossier", "&")}`, {
      method: "DELETE",
    }).then((body) => odbDossiers(body?.refreshed)),
  duplicateOdb: (code: string, unit?: string | null, name?: string | null) =>
    request<OdbDetail>(`/odbs/${encodeURIComponent(code)}/duplicate`, {
      method: "POST",
      body: JSON.stringify({ unit: unit ?? null, name: name ?? null }),
    }),
  importOdb: (document: unknown) =>
    request<OdbDetail>("/odbs/import", { method: "POST", body: JSON.stringify(document) }),
  odbExportUrl: (code: string) => `/api/odbs/${encodeURIComponent(code)}/export`,
  // --- ENI personnalisés (registre A135) : rangés par licence, 404 pour un ENI d'une autre licence. ---
  customEnemies: () => request<CustomEnemyList>("/custom-enemies"),
  customEnemy: (id: string) => request<CustomEnemyDetail>(`/custom-enemies/${id}`),
  /** 409 `CUSTOM_ENEMY_QUOTA` au-delà de cinq par licence. */
  createCustomEnemy: (code: string, name: string | null, sheet: CustomEnemySheet | null) =>
    request<CustomEnemyDetail>("/custom-enemies", { method: "POST", body: JSON.stringify({ code, name, sheet }) }),
  /** `version` : celle de l'ENI lu ; 412 `CUSTOM_ENEMY_CHANGED` si un autre poste l'a enregistré depuis. */
  updateCustomEnemy: (
    id: string,
    version: number,
    changes: { code?: string; name?: string; sheet?: CustomEnemySheet; scenarios?: Record<string, Partial<CustomScenario>> },
  ) => request<CustomEnemyDetail>(`/custom-enemies/${id}`, { method: "PUT", body: JSON.stringify({ ...changes, version }) }),
  generateCustomScenarios: (id: string, version: number) =>
    request<CustomEnemyDetail>(`/custom-enemies/${id}/scenarios/generate`, { method: "POST", body: JSON.stringify({ version }) }),
  /** Les quatorze modes d'action composés par le modèle, en arrière-plan (registre A137) : 409 si une composition tourne. */
  composeCustomScenarios: (id: string, version: number) =>
    request<CustomCompositionState>(`/custom-enemies/${id}/scenarios/compose`, { method: "POST", body: JSON.stringify({ version }) }),
  /** Avancement ; l'ENI n'est renvoyé que s'il a changé depuis `version`. */
  customComposition: (id: string, version?: number) =>
    request<CustomCompositionState>(
      `/custom-enemies/${id}/scenarios/compose${version !== undefined ? `?${new URLSearchParams({ version: String(version) })}` : ""}`,
    ),
  /** « Proposer un autre mode d'action » : le ME `key` recomposé par le modèle, en arrière-plan. */
  recomposeCustomScenario: (id: string, key: string, version: number) =>
    request<CustomCompositionState>(`/custom-enemies/${id}/scenarios/${encodeURIComponent(key)}/recompose`, {
      method: "POST",
      body: JSON.stringify({ version }),
    }),
  /** Proposition reformulée par le modèle de l'ENI (rien n'est enregistré). */
  rewriteCustomScenario: (id: string, key: string, language: "fr" | "en") =>
    request<{ key: string; language: string; text: string; eni_model: boolean }>(
      `/custom-enemies/${id}/scenarios/${encodeURIComponent(key)}/rewrite`,
      { method: "POST", body: JSON.stringify({ language }) },
    ),
  validateCustomEnemy: (id: string, version: number) =>
    request<CustomEnemyDetail>(`/custom-enemies/${id}/validate`, { method: "POST", body: JSON.stringify({ version }) }),
  customEnemyTraining: (id: string) => request<CustomTraining & { id: string; version: number }>(`/custom-enemies/${id}/training`),
  duplicateCustomEnemy: (id: string) =>
    request<CustomEnemyDetail>(`/custom-enemies/${id}/duplicate`, { method: "POST", body: JSON.stringify({}) }),
  /** Suppression ; forcée, rend les dossiers qui l'affrontaient (ils reprennent leur ennemi de référence). */
  deleteCustomEnemy: (id: string, force = false) =>
    request<
      | {
          detached?: { id: string; name: string }[];
          /** Effacement de tout ce qui décrivait l'ENI : dossiers et textes remis à zéro, versions d'ordres et
           *  RETEX effacés. */
          reset?: { dossiers: number; sections: number; versions: number; retex: number };
        }
      | undefined
    >(`/custom-enemies/${id}?force=${force}`, {
      method: "DELETE",
    }),
  /** `notice` : avis à montrer (texte gardé tronqué à ses 300 000 premiers signes), null sinon. */
  addCustomEnemyDocument: (id: string, file: File) =>
    request<CustomEnemyDetail & { notice?: string | null }>(`/custom-enemies/${id}/documents?${new URLSearchParams({ filename: file.name })}`, {
      method: "POST",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    }),
  removeCustomEnemyDocument: (id: string, documentId: string) =>
    request<CustomEnemyDetail>(`/custom-enemies/${id}/documents/${documentId}`, { method: "DELETE" }),
  missions: (level: Level, force: string | null, base: string | null = openDossier) =>
    request<MissionOption[]>(`/missions?${new URLSearchParams({ level, ...(force ? { force } : {}), ...(base ? { base } : {}) })}`),
  /** Déploiement T1/T2/T3 calculé par le serveur pour des paramètres donnés (dossier enregistré ou non). */
  deployment: (p: {
    level: Level;
    map_slug: string;
    odb?: string | null;
    mission?: string | null;
    enemy_type?: string | null;
    start_dtg?: string | null;
    time_zone?: string | null;
    opo?: number;
    rotation_deg?: number | null;
    /** Dossier enregistré dont part le brouillon (registre K19) : l'aperçu reprend ses déplacements, son fuseau
     *  personnalisé et ses missions choisies, conservés selon le §3.8 ; les avis sont dans `notices`. */
    base?: string | null;
    /** Ordre de bataille ennemi du brouillon (docs/14 §3.5). */
    eni_odb?: string | null;
    /** Langue du brouillon : toponymes d'une zone libre. */
    language?: "fr" | "en" | null;
  }) => {
    const query = new URLSearchParams({ level: p.level, map_slug: p.map_slug });
    for (const key of [
      "odb",
      "mission",
      "enemy_type",
      "start_dtg",
      "time_zone",
      "base",
      "eni_odb",
      "language",
    ] as const) {
      const value = p[key];
      if (value) query.set(key, value);
    }
    if (p.rotation_deg) query.set("rotation_deg", String(p.rotation_deg));
    return request<Deployment>(withOpo("/deployment", p.opo, query));
  },
  exerciseDeployment: (id: string, opo?: number) =>
    request<Deployment>(withOpo(`/exercises/${id}/deployment`, opo)),
  /** Ordre de bataille (composition des forces) pour des paramètres donnés : aperçu du brouillon. */
  orbat: (p: {
    level: Level;
    map_slug: string;
    odb?: string | null;
    mission?: string | null;
    enemy_type?: string | null;
    opo?: number;
    rotation_deg?: number | null;
    eni_odb?: string | null;
    language?: "fr" | "en" | null;
  }) => {
    const query = new URLSearchParams({ level: p.level, map_slug: p.map_slug });
    for (const key of ["odb", "mission", "enemy_type", "eni_odb", "language"] as const) {
      const value = p[key];
      if (value) query.set(key, value);
    }
    if (p.rotation_deg) query.set("rotation_deg", String(p.rotation_deg));
    if (openDossier) query.set("base", openDossier);
    return request<Orbat>(withOpo("/orbat", p.opo, query));
  },
  exerciseOrbat: (id: string, opo?: number) =>
    request<Orbat>(withOpo(`/exercises/${id}/orbat`, opo)),
  /** Niveaux inférieurs de la carte zoomée (registre J11) : unités des profondeurs 1 à `depth`, aux positions des
   *  OPO déclinés ; `status` « pending » (202) tant que le serveur les prépare. */
  exerciseSubordinates: (id: string, depth: number) =>
    request<Subordinates>(`/exercises/${id}/subordinates?depth=${depth}`),
  /** Déplace une unité à un temps (CENTAC : un ami pour tout l'OPO) ; renvoie le déploiement recalculé. */
  moveUnit: (
    ex: Pick<Exercise, "id" | "version">,
    body: {
      unit: string;
      phase: Phase;
      lon: number;
      lat: number;
      opo?: number;
    },
  ) =>
    request<Deployment>(`/exercises/${ex.id}/positions`, {
      method: "PUT",
      body: JSON.stringify(body),
      headers: { "If-Match": `"${ex.version}"` },
    }),
  /** Rôle d'une unité de l'ordre de bataille ennemi dans ce dossier (docs/14 §3.4) : les emplacements de la
   *  manœuvre et les échelons ennemis de l'ordre suivent ; `role` null rend celui de l'ordre de bataille. */
  setEniRole: (ex: Pick<Exercise, "id" | "version">, unit: string, role: EniRole | null) =>
    request<Deployment>(`/exercises/${ex.id}/eni-roles`, {
      method: "PUT",
      body: JSON.stringify({ unit, role }),
      headers: { "If-Match": `"${ex.version}"` },
    }),
  /** Composition du dossier (docs/18 §4.1) : ajouter, retirer, rétablir une unité, ou tout rétablir d'un camp ;
   *  le catalogue et les ordres de bataille restent inchangés. Renvoie le déploiement du dossier (composition, OPO
   *  recomposés, paragraphes à revoir). */
  setComposition: (ex: Pick<Exercise, "id" | "version">, body: CompositionWrite, opo?: number) =>
    request<Deployment>(withOpo(`/exercises/${ex.id}/composition`, opo), {
      method: "PUT",
      body: JSON.stringify(body),
      headers: { "If-Match": `"${ex.version}"` },
    }),
  /** Rôle d'une unité amie ou ennemie dans ce dossier (docs/18 §4.2) ; `role` null rend le rôle par défaut. */
  setRole: (ex: Pick<Exercise, "id" | "version">, body: RoleWrite, opo?: number) =>
    request<Deployment>(withOpo(`/exercises/${ex.id}/roles`, opo), {
      method: "PUT",
      body: JSON.stringify(body),
      headers: { "If-Match": `"${ex.version}"` },
    }),
  /** Unités proposées par « Ajouter une unité » (docs/18 §4.3) : même ordre de bataille, catalogue, ordres de
   *  bataille construits visibles. */
  compositionCandidates: (
    exerciseId: string,
    p: { side: CompositionSide; origin: "same" | "catalogue" | "odb"; q?: string; limit?: number },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams({ side: p.side, origin: p.origin });
    if (p.q?.trim()) query.set("q", p.q.trim().slice(0, 60));
    if (p.limit) query.set("limit", String(Math.min(100, p.limit)));
    return request<CompositionCandidates>(`/exercises/${exerciseId}/composition/candidates?${query}`, { signal });
  },
  /** Détruit ou rétablit un pont du camp ; renvoie le déploiement du dossier (ponts détruits compris). */
  setBridge: (
    ex: Pick<Exercise, "id" | "version">,
    key: string,
    destroyed: boolean,
    opo?: number,
  ) =>
    request<Deployment>(withOpo(`/exercises/${ex.id}/bridges`, opo), {
      method: "PUT",
      body: JSON.stringify({ key, destroyed }),
      headers: { "If-Match": `"${ex.version}"` },
    }),
  /** Remplace les graphiques tactiques du dossier (état complet) ; renvoie le déploiement du dossier. */
  setGraphics: (
    ex: Pick<Exercise, "id" | "version">,
    graphics: TacticalGraphics,
    opo?: number,
  ) =>
    request<Deployment>(withOpo(`/exercises/${ex.id}/graphics`, opo), {
      method: "PUT",
      body: JSON.stringify(graphics),
      headers: { "If-Match": `"${ex.version}"` },
    }),
  graphicsCatalog: () => request<GraphicsCatalog>("/graphics/catalog"),
  /** Remplace les annotations APP-6 du dossier (état complet) ; les ordres suivent la carte. */
  setAnnotations: (ex: Pick<Exercise, "id" | "version">, items: Annotation[], opo?: number) =>
    request<Deployment>(withOpo(`/exercises/${ex.id}/annotations`, opo), {
      method: "PUT",
      body: JSON.stringify({ items }),
      headers: { "If-Match": `"${ex.version}"` },
    }),
  /** Potentiel d'une unité (0 à 1,1 ; null : nominal), comme le curseur POTENTIEL de l'iPad. */
  setStrength: (ex: Pick<Exercise, "id" | "version">, unit: string, value: number | null, opo?: number) =>
    request<Deployment>(withOpo(`/exercises/${ex.id}/strengths`, opo), {
      method: "PUT",
      body: JSON.stringify({ unit, value }),
      headers: { "If-Match": `"${ex.version}"` },
    }),
  /** Coefficients de mobilité locale en quelques points ([lon, lat]) : mesure distance et temps. */
  mobility: (
    slug: string,
    body: { points: [number, number][]; start_dtg?: string | null; time_zone?: string | null; weather_scenario?: string | null },
  ) =>
    request<{
      base: number;
      month: number;
      coefficients: number[];
      /** Mêmes points par beau temps et majoration des ordres (ligne « Délais ») : temps = beau temps × (1 + %). */
      fair_coefficients?: number[];
      delay_pct?: number;
      level: string;
      tone: "ok" | "warn" | "brand";
    }>(
      `/maps/${slug}/mobility${clockParam()}`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  /** Sélecteur Météo (Données initiales, étape Terrain) : conditions possibles pour la carte et le mois, verdict sur
   *  le choix envoyé, aperçu de la météo appliquée, éphéméride ; sans date, début à la création du dossier ouvert. */
  weatherOptions: (
    slug: string,
    body: { start_dtg: string | null; time_zone: string | null; weather_scenario: string | null; weather_custom: WeatherCustom | null },
  ) =>
    request<WeatherOptions>(`/maps/${slug}/weather${clockParam()}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** Météo proposée pour un lieu nommé avant le placement de son terrain (`POST /places/weather`) : même forme que
   *  `weatherOptions`, sur le climat estimé d'après les coordonnées (`approximate`). */
  placeWeather: (body: {
    lon: number;
    lat: number;
    start_dtg: string | null;
    time_zone: string | null;
    weather_scenario: string | null;
    weather_custom: WeatherCustom | null;
  }) =>
    request<WeatherOptions>(`/places/weather${clockParam()}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** Recherche de localités dans le répertoire hors ligne du serveur (champ « Lieu du nouveau terrain ») : la saisie
   *  voyage dans le corps, jamais dans l'adresse ; `country` (ISO 3166-1 alpha-2, pays de la force) départage les
   *  homonymes à égalité. */
  searchPlaces: (body: { text: string; limit: number; country?: string }, signal?: AbortSignal) =>
    request<PlaceSearchResult>("/places/search", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),
  /** Analyse du terrain d'une zone (ou de l'emprise du camp si `polygon` est nul), axe favorable sur demande. */
  terrainAnalysis: (slug: string, body: TerrainAnalysisRequest) =>
    request<TerrainReport>(`/maps/${slug}/terrain/analysis${clockParam()}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** Remplace l'état complet des déplacements (annulation, retour au dispositif doctrinal). Corps construit par
   *  `placementBody` : `corridor`, `missions`, `anchored` et `measures` absents (`undefined`) = conservés, null =
   *  supprimés. Un instantané d'annulation passe ces quatre clés explicitement (`?? null`), sinon le premier ⌘Z
   *  après une première retouche ne déferait rien. */
  setPlacement: (
    ex: Pick<Exercise, "id" | "version">,
    body: Placement,
    opo?: number,
  ) =>
    request<Deployment>(withOpo(`/exercises/${ex.id}/placement`, opo), {
      method: "PUT",
      body: JSON.stringify(placementBody(body)),
      headers: { "If-Match": `"${ex.version}"` },
    }),
  /** Remplace le fuseau personnalisé (état complet, docs/14 §3.1 ; `seed` et `prior_lines` ne sont pas envoyés,
   *  le serveur les calcule). 422 avec le message de la règle si le fuseau est refusé. */
  setCorridor: (ex: Pick<Exercise, "id" | "version">, corridor: CorridorModel) =>
    request<Deployment>(`/exercises/${ex.id}/corridor`, {
      method: "PUT",
      body: JSON.stringify(corridorBody(corridor)),
      headers: { "If-Match": `"${ex.version}"` },
    }),
  /** Revient au fuseau automatique (camp : LIMA glissées d'avant la retouche rétablies) ou au fuseau de la zone. */
  resetCorridor: (ex: Pick<Exercise, "id" | "version">) =>
    request<Deployment>(`/exercises/${ex.id}/corridor`, {
      method: "DELETE",
      headers: { "If-Match": `"${ex.version}"` },
    }),
  /** Mission d'une unité à un temps (docs/14 §4.1) : `mission` null = mission doctrinale (selon le rôle, retire
   *  aussi l'ancrage), « » = sans mission ; `reposition` : placer l'unité selon la mission (ancrage K15) ou garder
   *  sa position. */
  setMission: (ex: Pick<Exercise, "id" | "version">, body: MissionChoice) =>
    request<Deployment>(`/exercises/${ex.id}/missions`, {
      method: "PUT",
      body: JSON.stringify({ unit: body.unit, phase: body.phase, mission: body.mission, reposition: body.reposition }),
      headers: { "If-Match": `"${ex.version}"` },
    }),
  /** Rend à des unités les missions d'avant une propagation (annulation d'une ligne de l'avis, docs/25 §6) :
   *  missions choisies telles quelles, posture inchangée. */
  restoreMissions: (ex: Pick<Exercise, "id" | "version">, items: { unit: string; phase: Phase; mission: string }[]) =>
    request<Deployment>(`/exercises/${ex.id}/missions/restore`, {
      method: "POST",
      body: JSON.stringify({ items }),
      headers: { "If-Match": `"${ex.version}"` },
    }),
  /** Fait glisser une ligne de coordination le long du fuseau jusqu'au point donné (GDH décalé). */
  moveLine: (
    ex: Pick<Exercise, "id" | "version">,
    body: { line: PhaseLine["name"]; lon: number; lat: number },
    opo?: number,
  ) =>
    request<Deployment>(withOpo(`/exercises/${ex.id}/lines`, opo), {
      method: "PUT",
      body: JSON.stringify(body),
      headers: { "If-Match": `"${ex.version}"` },
    }),
  /** Rétablit la position doctrinale (et le GDH) d'une ligne, ou de toutes. */
  resetLines: (
    ex: Pick<Exercise, "id" | "version">,
    line?: PhaseLine["name"],
    opo?: number,
  ) =>
    request<Deployment>(
      withOpo(
        `/exercises/${ex.id}/lines`,
        opo,
        new URLSearchParams(line ? { line } : {}),
      ),
      {
        method: "DELETE",
        headers: { "If-Match": `"${ex.version}"` },
      },
    ),
  /** Position d'une ligne de la ZO (docs/15 §3.7) : choisie, calculée (`ends: null`), retirée ou rétablie
   *  (`off`, CFL et FSCL). 422 avec le message de la règle violée (structure, ordre des lignes limité à la ligne
   *  modifiée, CENTAC) ; l'OPO n'est pas recomposé. Avis « CFL retirée », « FSCL retirée ». */
  setMeasure: (ex: Pick<Exercise, "id" | "version">, change: MeasureChange, opo?: number) =>
    request<Deployment>(withOpo(`/exercises/${ex.id}/measures`, opo), {
      method: "PUT",
      body: JSON.stringify(measureBody(change)),
      headers: { "If-Match": `"${ex.version}"` },
    }),
  /** Toutes les lignes de la ZO reviennent à leurs positions calculées (CFL et FSCL retirées comprises). Avis
   *  « Lignes et zones de la ZO replacées selon le dispositif ». */
  resetMeasures: (ex: Pick<Exercise, "id" | "version">, opo?: number) =>
    request<Deployment>(withOpo(`/exercises/${ex.id}/measures`, opo), {
      method: "DELETE",
      headers: { "If-Match": `"${ex.version}"` },
    }),
  /** Rétablit la position doctrinale d'une unité (à un temps ou à tous), ou de tout le dispositif. */
  resetPositions: (
    ex: Pick<Exercise, "id" | "version">,
    target: { unit?: string; phase?: Phase } = {},
    opo?: number,
  ) => {
    const query = new URLSearchParams();
    if (target.unit) query.set("unit", target.unit);
    if (target.phase) query.set("phase", target.phase);
    return request<Deployment>(
      withOpo(`/exercises/${ex.id}/positions`, opo, query),
      {
        method: "DELETE",
        headers: { "If-Match": `"${ex.version}"` },
      },
    );
  },
  // --- Dossier sur l'ordinateur du client (fichier « .generex ») ---
  // Ce que le fichier « .generex » laisserait de côté (audit M11) : lu avant de supprimer un dossier.
  exportCompleteness: (exerciseId: string) =>
    request<ExportCompleteness>(`/exercises/${exerciseId}/export/completeness`),
  exerciseDownloadUrl: (exerciseId: string) =>
    `/api/exercises/${exerciseId}/export`,
  /** `lang` : langue de l'interface, pour le suffixe d'un nom déjà pris (« (importé le …) », « (imported …) »). */
  importExercise: (payload: unknown) =>
    request<Exercise>(`/exercises/import?${new URLSearchParams({ lang: lang() })}`, {
      method: "POST",
      body: JSON.stringify(payload),
    }).then(announced),
  /** Import d'un OPO (docs/20-import-opo.md) : fichier envoyé tel quel ; l'aperçu ne garde rien, la création renvoie le
   *  même fichier (aucun fichier n'est gardé entre les deux). */
  previewOpoImport: (file: File) =>
    request<OpoImportPreview>(`/exercises/import-opo/preview?${new URLSearchParams({ filename: file.name, lang: lang() })}`, {
      method: "POST",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    }),
  importOpo: (file: File) =>
    request<Exercise>(`/exercises/import-opo?${new URLSearchParams({ filename: file.name, lang: lang() })}`, {
      method: "POST",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    }).then(announced),
  /** OPO sans données Generex (docs/20 §12) : vérification ouverte (fichier, ou texte collé), rouverte, corrigée
   *  (version contrôlée : 409 si un autre poste a écrit entre-temps), abandonnée, puis dossier créé. */
  openOpoDraft: (file: File) =>
    request<OpoDraftState>(`/exercises/import-opo/drafts?${new URLSearchParams({ filename: file.name, lang: lang() })}`, {
      method: "POST",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    }),
  pasteOpoDraft: (text: string) =>
    request<OpoDraftState>(`/exercises/import-opo/drafts?${new URLSearchParams({ filename: "texte-colle.txt", paste: "true", lang: lang() })}`, {
      method: "POST",
      headers: { "Content-Type": "text/plain; charset=utf-8" },
      body: text,
    }),
  opoDrafts: () => request<OpoDraftSummary[]>("/exercises/import-opo/drafts"),
  opoDraft: (id: string) => request<OpoDraftState>(`/exercises/import-opo/drafts/${id}?${new URLSearchParams({ lang: lang() })}`),
  saveOpoDraft: (id: string, version: number, draft: OpoDraft) =>
    request<OpoDraftState>(`/exercises/import-opo/drafts/${id}?${new URLSearchParams({ lang: lang() })}`, {
      method: "PUT",
      body: JSON.stringify({ version, draft }),
    }),
  /** Calque KMZ, KML ou NVG joint à l'ordre : son fuseau et ses LIMA remplacent le fuseau proposé. */
  opoDraftOverlay: (id: string, version: number, file: File) =>
    request<OpoDraftState>(
      `/exercises/import-opo/drafts/${id}/overlay?${new URLSearchParams({ filename: file.name, version: String(version), lang: lang() })}`,
      { method: "POST", headers: { "Content-Type": file.type || "application/octet-stream" }, body: file },
    ),
  /** Terrain libre d'un ordre hors des camps (docs/20 §14.7) : préparer (emprise proposée, ou axe ajusté), reprendre un
   *  terrain de la licence, étendre, relancer, retirer ; rend la vérification relue et l'avis à montrer. */
  opoDraftTerrain: (
    id: string,
    version: number,
    action: "prepare" | "reuse" | "extend" | "retry" | "release",
    extra: { zone_id?: string; axis?: { start: [number, number]; end: [number, number] }; name?: string } = {},
  ) =>
    // Reprendre ou retirer un terrain (`…/terrain/link`) n'en prépare aucun : chemin à part, hors du quota des zones.
    request<OpoDraftState & { notice: string }>(
      `/exercises/import-opo/drafts/${id}/terrain${action === "reuse" || action === "release" ? "/link" : ""}` +
        `?${new URLSearchParams({ lang: lang() })}`,
      { method: "POST", body: JSON.stringify({ version, action, ...extra }) },
    ),
  deleteOpoDraft: (id: string) => request<void>(`/exercises/import-opo/drafts/${id}`, { method: "DELETE" }),
  createFromOpoDraft: (id: string, version: number) =>
    request<Exercise>(`/exercises/import-opo/drafts/${id}/create?${new URLSearchParams({ lang: lang() })}`, {
      method: "POST",
      body: JSON.stringify({ version }),
    }).then(announced),
  /** Réseau de manœuvre du fuseau (rocades, pénétrantes), en GeoJSON. */
  /** ZEF et ZEA de l'annexe A de l'OPO de division (GeoJSON), recalculées sur l'état courant du dossier. */
  fireZones: (exerciseId: string) => request<FeatureCollection>(`/exercises/${exerciseId}/order/fire-zones`),
  tacticalRoads: (exerciseId: string, opo = 1) =>
    request<FeatureCollection>(`/exercises/${exerciseId}/tactical-roads?opo=${opo}`),
  // --- Modes d'action ennemis ---
  enemyCoa: (exerciseId: string, opo?: number) =>
    request<EnemyCoa>(withOpo(`/exercises/${exerciseId}/coa`, opo)),
  // --- DIREX ---
  direx: (exerciseId: string, opo?: number) => request<Direx>(withOpo(`/exercises/${exerciseId}/direx`, opo)),
  generateMelMil: (
    exerciseId: string,
    body: { tempos: string[]; count: number; type?: string | null; cellule?: string | null },
    opo?: number,
  ) =>
    request<Direx>(withOpo(`/exercises/${exerciseId}/direx/generate`, opo), {
      method: "POST",
      body: JSON.stringify(body),
    }),
  createIncident: (exerciseId: string, body: NewIncident, opo?: number) =>
    request<Direx>(withOpo(`/exercises/${exerciseId}/direx/incidents`, opo), {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateIncident: (exerciseId: string, incidentId: string, changes: IncidentChanges, opo?: number) =>
    request<Direx>(withOpo(`/exercises/${exerciseId}/direx/incidents/${incidentId}`, opo), {
      method: "PATCH",
      body: JSON.stringify(changes),
    }),
  /** Incident proposé par une annotation de la carte : retenu, écarté ou rétabli (docs/08 §19). */
  decideProposal: (exerciseId: string, incidentId: string, decision: "keep" | "dismiss" | "restore", version?: number, opo?: number) =>
    request<Direx>(withOpo(`/exercises/${exerciseId}/direx/incidents/${incidentId}/decision`, opo), {
      method: "POST",
      body: JSON.stringify({ decision, version }),
    }),
  keepProposals: (exerciseId: string, opo?: number) =>
    request<Direx>(withOpo(`/exercises/${exerciseId}/direx/proposals/keep`, opo), { method: "POST" }),
  deleteIncident: (exerciseId: string, incidentId: string, opo?: number) =>
    request<Direx>(withOpo(`/exercises/${exerciseId}/direx/incidents/${incidentId}`, opo), { method: "DELETE" }),
  direxClock: (
    exerciseId: string,
    action: "start" | "pause" | "reset" | "speed" | "set" | "auto_inject",
    value?: unknown,
    opo?: number,
  ) =>
    request<Direx>(withOpo(`/exercises/${exerciseId}/direx/clock`, opo), {
      method: "POST",
      body: JSON.stringify({ action, value }),
    }),
  direxRole: (exerciseId: string, role: DirexRole, cellule: string | null, opo?: number) =>
    request<Direx>(withOpo(`/exercises/${exerciseId}/direx/role`, opo), {
      method: "PUT",
      body: JSON.stringify({ role, cellule }),
    }),
  // --- 3A et point de situation ---
  aar: (exerciseId: string, opo?: number) => request<Aar>(withOpo(`/exercises/${exerciseId}/aar`, opo)),
  /** `base` : date de modification de la note lue (null : pas encore notée) ; 409 `CHANGED_ELSEWHERE` si un autre
   *  poste l'a modifiée depuis. */
  evaluateIncident: (
    exerciseId: string,
    incidentId: string,
    ratings: number[],
    remarks: string,
    base: string | null,
    opo?: number,
  ) =>
    request<Aar>(withOpo(`/exercises/${exerciseId}/aar/incidents/${incidentId}`, opo), {
      method: "PUT",
      body: JSON.stringify({ ratings, remarks, base }),
    }),
  clearAarTarget: (exerciseId: string, key: string, opo?: number) =>
    request<Aar>(withOpo(`/exercises/${exerciseId}/aar/target`, opo, new URLSearchParams({ key })), {
      method: "DELETE",
    }),
  evalopsUrl: (exerciseId: string, target: string, opo?: number) =>
    `/api${withOpo(`/exercises/${exerciseId}/aar/evalops.pdf`, opo, new URLSearchParams({ target }))}`,
  situationBoard: (exerciseId: string, opo?: number) =>
    request<SituationBoard>(withOpo(`/exercises/${exerciseId}/direx/situation`, opo)),
  updateCell: (
    exerciseId: string,
    cellule: string,
    // `base` : date de modification du point lue (null : jamais renseigné), contrôlée par le serveur (409).
    changes: Partial<Pick<SituationCell, "color" | "note" | "alert" | "question" | "enabled" | "dto" | "capops">> & {
      base?: string | null;
    },
    opo?: number,
  ) =>
    request<SituationBoard>(withOpo(`/exercises/${exerciseId}/direx/cells/${encodeURIComponent(cellule)}`, opo), {
      method: "PATCH",
      body: JSON.stringify(changes),
    }),
  // --- Exports de la situation ---
  situationSummary: (exerciseId: string, phase: SituationPhase, opo?: number) =>
    request<SituationSummary>(
      withOpo(`/exercises/${exerciseId}/exports`, opo, new URLSearchParams({ phase })),
    ),
  // --- Traductions des documents lus dans l'autre langue (docs/13) ---
  /** « Réessayer » : traductions en échec du dossier relancées. */
  retryTranslations: (exerciseId: string) =>
    request<{ retried: number }>(`/exercises/${exerciseId}/translations/retry`, { method: "POST" }),
  // --- Dossier d'exercice (Partie I de l'iPad) ---
  briefing: (exerciseId: string) => request<Briefing>(`/exercises/${exerciseId}/briefing`),
  editBriefing: (exerciseId: string, version: number, sections: Record<string, string>) =>
    request<Briefing>(`/exercises/${exerciseId}/briefing`, {
      method: "PATCH",
      body: JSON.stringify({ version, sections }),
    }),
  resetBriefingSection: (exerciseId: string, section: string) =>
    request<Briefing>(`/exercises/${exerciseId}/briefing/sections/${section}/reset`, { method: "POST" }),
  // Arrêt voulu de la rédaction (audit m10) : ses paragraphes vides ne sont pas repris d'office.
  stopBriefing: (exerciseId: string) =>
    request<void>(`/exercises/${exerciseId}/briefing/stop`, { method: "POST" }),
  briefingExportUrl: (exerciseId: string, fmt: "pdf" | "docx" | "odt" | "txt") =>
    `/api/exercises/${exerciseId}/briefing/export/${fmt}`,
  // --- Fiche pays du dossier d'exercice ---
  countrySheet: (exerciseId: string) => request<CountrySheet>(`/exercises/${exerciseId}/country-sheet`),
  editCountrySheet: (exerciseId: string, version: number, sections: Record<string, string>) =>
    request<CountrySheet>(`/exercises/${exerciseId}/country-sheet`, {
      method: "PATCH",
      body: JSON.stringify({ version, sections }),
    }),
  resetCountrySection: (exerciseId: string, section: string) =>
    request<CountrySheet>(`/exercises/${exerciseId}/country-sheet/sections/${section}/reset`, { method: "POST" }),
  // Arrêt voulu de la rédaction : ses rubriques en attente ne sont pas reprises d'office.
  stopCountrySheet: (exerciseId: string) =>
    request<void>(`/exercises/${exerciseId}/country-sheet/stop`, { method: "POST" }),
  countrySheetExportUrl: (exerciseId: string, fmt: "pdf" | "docx" | "odt" | "txt") =>
    `/api/exercises/${exerciseId}/country-sheet/export/${fmt}`,
  /** Dossier d'exercice complet (fiche pays, mise en ambiance, OPO validé) en un seul PDF. */
  dossierExportUrl: (exerciseId: string, number = 1) =>
    `/api/exercises/${exerciseId}/dossier/export/pdf?number=${number}`,
  /** Genèse suivie du dossier complet (registre F37) : lancée ou rejointe, état publié, dernière genèse du dossier
   *  (rattachement après rechargement) et fichier produit. */
  startDossierJob: (exerciseId: string, number = 1) =>
    request<DossierJob>(`/exercises/${exerciseId}/dossier/export/jobs?number=${number}`, { method: "POST" }),
  dossierJob: (exerciseId: string, jobId: string) =>
    request<DossierJob>(`/exercises/${exerciseId}/dossier/export/jobs/${encodeURIComponent(jobId)}`),
  currentDossierJob: (exerciseId: string) =>
    request<DossierJob | null>(`/exercises/${exerciseId}/dossier/export/jobs/current`),
  dossierJobFileUrl: (exerciseId: string, jobId: string) =>
    `/api/exercises/${exerciseId}/dossier/export/jobs/${encodeURIComponent(jobId)}/file`,
  /** Carte du pays ou des commandements : son empreinte (`v`) dans l'adresse, une carte changée change d'adresse. */
  countrySheetMapUrl: (exerciseId: string, which: "country" | "commands", v: string) =>
    `/api/exercises/${exerciseId}/country-sheet/map/${which}?${new URLSearchParams({ v })}`,
  /** Frontières des pays fictifs de la fiche (GeoJSON), calque de la carte : même empreinte de carte (`v`). */
  countryBorders: (exerciseId: string, v: string) =>
    request<FeatureCollection>(`/exercises/${exerciseId}/country-sheet/borders?${new URLSearchParams({ v })}`),
  // --- Ordre d'opérations (number = OPO de la rotation CENTAC, 1 ailleurs) ---
  order: (exerciseId: string, number = 1) =>
    request<Order>(`/exercises/${exerciseId}/order?number=${number}`),
  generateOrder: (
    exerciseId: string,
    body: { force?: boolean; keep_edits?: boolean } = {},
    number = 1,
  ) =>
    request<Order>(`/exercises/${exerciseId}/order/generate`, {
      method: "POST",
      body: JSON.stringify({ ...body, number }),
    }),
  orderLayout: (exerciseId: string, number = 1) =>
    request<OrderLayout>(
      `/exercises/${exerciseId}/order/layout?number=${number}`,
    ),
  orderFields: (exerciseId: string, number = 1) =>
    request<OrderFieldSpecs>(
      `/exercises/${exerciseId}/order/fields?number=${number}`,
    ),
  /** Champs de l'ordre. `keepalive` : la requête survit à la page (arrière-plan, fermeture de l'onglet ; corps de
   *  64 Kio au plus, voir views/order/orderSaveQueue.ts). */
  editOrder: (
    exerciseId: string,
    version: number,
    fields: Record<string, unknown>,
    number = 1,
    keepalive = false,
  ) =>
    request<Order>(`/exercises/${exerciseId}/order?number=${number}`, {
      method: "PATCH",
      body: JSON.stringify({ version, fields }),
      ...(keepalive ? { keepalive: true } : {}),
    }),
  resetOrderField: (exerciseId: string, field: string, number = 1) =>
    request<Order>(
      `/exercises/${exerciseId}/order/reset-field?${new URLSearchParams({ field, number: String(number) })}`,
      {
        method: "POST",
      },
    ),
  /** « Garder mon texte » (docs/18 §5.4) : le paragraphe modifié à la main n'est plus signalé à revoir ; contrôle de
   *  version comme une modification. */
  keepOrderField: (exerciseId: string, field: string, number: number, version: number) =>
    request<Order>(
      `/exercises/${exerciseId}/order/keep-field?${new URLSearchParams({ field, number: String(number), version: String(version) })}`,
      { method: "POST" },
    ),
  /** « Masquer les repères » : les repères des paragraphes mis à jour tombent pour tous les OPO du dossier (texte
   *  inchangé) ; rend l'OPO `number`. */
  orderChangesSeen: (exerciseId: string, number = 1) =>
    request<Order>(`/exercises/${exerciseId}/order/changes/seen?number=${number}`, { method: "POST" }),
  checkOrder: (exerciseId: string, number = 1) =>
    request<CoherenceReport>(
      `/exercises/${exerciseId}/order/check?number=${number}`,
      { method: "POST" },
    ),
  /** Validation de l'ordre ; `override` : justification du passer outre aux constats de cohérence bloquants
   *  (docs/24 §6), mémorisée dans le dossier. */
  finalizeOrder: (exerciseId: string, number = 1, override?: string) =>
    request<Order>(`/exercises/${exerciseId}/order/finalize?number=${number}`, {
      method: "POST",
      ...(override ? { body: JSON.stringify({ override }) } : {}),
    }),
  /** Rapport de cohérence tactique du dossier (docs/24). */
  coherence: (exerciseId: string, opo = 1) =>
    request<TacticalCoherence>(withOpo(`/exercises/${exerciseId}/coherence`, opo)),
  /** Accepte un constat avec sa justification, mémorisée dans le dossier ; rend le rapport à jour. */
  acceptFinding: (exerciseId: string, finding: string, justification: string, opo = 1) =>
    request<TacticalCoherence>(`/exercises/${exerciseId}/coherence/accept`, {
      method: "POST",
      body: JSON.stringify({ finding, justification, opo }),
    }),
  /** Retire l'acceptation d'un constat ; rend le rapport à jour. */
  revokeFinding: (exerciseId: string, finding: string, opo = 1) =>
    request<TacticalCoherence>(withOpo(`/exercises/${exerciseId}/coherence/accept/${encodeURIComponent(finding)}`, opo), {
      method: "DELETE",
    }),

  reopenOrder: (exerciseId: string, number = 1) =>
    request<Order>(`/exercises/${exerciseId}/order/reopen?number=${number}`, {
      method: "POST",
    }),
  signOrder: (exerciseId: string, pngBase64: string, number = 1) =>
    request<Order>(
      `/exercises/${exerciseId}/order/signature?number=${number}`,
      {
        method: "PUT",
        body: JSON.stringify({ png_base64: pngBase64 }),
      },
    ),
  unsignOrder: (exerciseId: string, number = 1) =>
    request<Order>(
      `/exercises/${exerciseId}/order/signature?number=${number}`,
      { method: "DELETE" },
    ),
  // --- Rotation CENTAC ---
  rotation: (exerciseId: string) =>
    request<RotationSummary>(`/exercises/${exerciseId}/rotation`),
  saveRotationResults: (
    exerciseId: string,
    number: number,
    entry: Partial<RotationResults>,
  ) =>
    request<RotationSummary>(
      `/exercises/${exerciseId}/rotation/results/${number}`,
      {
        method: "PUT",
        body: JSON.stringify(entry),
      },
    ),
  wingoPreview: (exerciseId: string, number: number) =>
    request<WingoPreview>(`/exercises/${exerciseId}/rotation/wingo/${number}`),
  applyWingo: (
    exerciseId: string,
    number: number,
    body: {
      results: Partial<RotationResults>;
      contextual: Record<string, Record<string, string>>;
      regenerate: boolean;
    },
  ) =>
    request<WingoApplied>(`/exercises/${exerciseId}/rotation/wingo/${number}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // --- WINGO de la rotation CENTAC, documents (registre A120) ---
  wingo: (exerciseId: string, number: number) => request<Wingo>(`/exercises/${exerciseId}/wingos/${number}`),
  wingoLayout: (exerciseId: string, number: number) =>
    request<OrderLayout>(`/exercises/${exerciseId}/wingos/${number}/layout`),
  finalizeWingo: (exerciseId: string, number: number) =>
    request<Wingo>(`/exercises/${exerciseId}/wingos/${number}/finalize`, { method: "POST" }),
  reopenWingo: (exerciseId: string, number: number) =>
    request<Wingo>(`/exercises/${exerciseId}/wingos/${number}/reopen`, { method: "POST" }),
  signWingo: (exerciseId: string, number: number, png: string) =>
    request<Wingo>(`/exercises/${exerciseId}/wingos/${number}/signature`, {
      method: "PUT",
      body: JSON.stringify({ png_base64: png }),
    }),
  unsignWingo: (exerciseId: string, number: number) =>
    request<Wingo>(`/exercises/${exerciseId}/wingos/${number}/signature`, { method: "DELETE" }),
  // --- FRAGO de la rotation CENTAC (docs/08 §27) ---
  fragos: (exerciseId: string) => request<FragoSummary>(`/exercises/${exerciseId}/fragos`),
  createFrago: (exerciseId: string, body: { opo: number; subject?: string; incident_id?: string | null; date_time?: string | null }) =>
    request<Frago>(`/exercises/${exerciseId}/fragos`, { method: "POST", body: JSON.stringify(body) }),
  frago: (exerciseId: string, number: number) => request<Frago>(`/exercises/${exerciseId}/fragos/${number}`),
  fragoLayout: (exerciseId: string, number: number) =>
    request<OrderLayout>(`/exercises/${exerciseId}/fragos/${number}/layout`),
  fragoFields: (exerciseId: string, number: number) =>
    request<{ fields: FragoFieldSpec[] }>(`/exercises/${exerciseId}/fragos/${number}/fields`),
  editFrago: (exerciseId: string, number: number, version: number, fields: Record<string, string>) =>
    request<Frago>(`/exercises/${exerciseId}/fragos/${number}`, {
      method: "PATCH",
      body: JSON.stringify({ version, fields }),
    }),
  resetFragoField: (exerciseId: string, number: number, field: string) =>
    request<Frago>(`/exercises/${exerciseId}/fragos/${number}/reset-field`, {
      method: "POST",
      body: JSON.stringify({ field }),
    }),
  recomposeFrago: (exerciseId: string, number: number) =>
    request<Frago>(`/exercises/${exerciseId}/fragos/${number}/recompose`, { method: "POST" }),
  finalizeFrago: (exerciseId: string, number: number, override?: string) =>
    request<Frago>(`/exercises/${exerciseId}/fragos/${number}/finalize`, {
      method: "POST",
      ...(override ? { body: JSON.stringify({ override }) } : {}),
    }),
  reopenFrago: (exerciseId: string, number: number) =>
    request<Frago>(`/exercises/${exerciseId}/fragos/${number}/reopen`, { method: "POST" }),
  deleteFrago: (exerciseId: string, number: number) =>
    request<void>(`/exercises/${exerciseId}/fragos/${number}`, { method: "DELETE" }),
  signFrago: (exerciseId: string, number: number, png_base64: string) =>
    request<Frago>(`/exercises/${exerciseId}/fragos/${number}/signature`, {
      method: "PUT",
      body: JSON.stringify({ png_base64 }),
    }),
  unsignFrago: (exerciseId: string, number: number) =>
    request<Frago>(`/exercises/${exerciseId}/fragos/${number}/signature`, { method: "DELETE" }),
  regenerateFromWingo: (exerciseId: string, number: number) =>
    request<Order>(`/exercises/${exerciseId}/rotation/regenerate/${number}`, {
      method: "POST",
    }),
  orderVersions: (exerciseId: string, number = 1) =>
    request<OrderVersion[]>(
      `/exercises/${exerciseId}/order/versions?number=${number}`,
    ),
  restoreOrderVersion: (exerciseId: string, version: number, number = 1) =>
    request<Order>(
      `/exercises/${exerciseId}/order/versions/${version}/restore?number=${number}`,
      { method: "POST" },
    ),
  importOrder: (exerciseId: string, payload: unknown) =>
    request<Order>(`/exercises/${exerciseId}/order/import`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  receivedOrders: (exerciseId: string) =>
    request<ReceivedSummary[]>(`/exercises/${exerciseId}/received-orders`),
  receivedOrder: (exerciseId: string, orderId: string) =>
    request<ReceivedOrder>(`/exercises/${exerciseId}/received-orders/${orderId}`),
  pasteReceivedOrder: (exerciseId: string, text: string, name?: string) =>
    request<ReceivedOrder>(`/exercises/${exerciseId}/received-orders`, {
      method: "POST",
      body: JSON.stringify({ text, name: name || null }),
    }),
  /** Fichier envoyé tel quel (PDF, Word, LibreOffice, RTF, texte) ; le serveur en lit le texte. */
  uploadReceivedOrder: (exerciseId: string, file: File, name?: string) => {
    const query = new URLSearchParams({ filename: file.name });
    if (name) query.set("name", name);
    return request<ReceivedOrder>(`/exercises/${exerciseId}/received-orders/file?${query}`, {
      method: "POST",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    });
  },
  renameReceivedOrder: (exerciseId: string, orderId: string, name: string) =>
    request<ReceivedOrder>(`/exercises/${exerciseId}/received-orders/${orderId}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),
  deleteReceivedOrder: (exerciseId: string, orderId: string) =>
    request<void>(`/exercises/${exerciseId}/received-orders/${orderId}`, { method: "DELETE" }),
  receivedOriginalUrl: (exerciseId: string, orderId: string) =>
    `/api/exercises/${exerciseId}/received-orders/${orderId}/original`,
  /** Report d'un ordre reçu dans les Données initiales ; les avis du dossier (météo ramenée au terrain, CENTAC) sont
   *  annoncés à la carte, qui les joint à l'avis du report. */
  applyReceivedOrder: (exerciseId: string, orderId: string, body: ReceivedApply) =>
    request<{ exercise: Exercise; order: ReceivedOrder }>(
      `/exercises/${exerciseId}/received-orders/${orderId}/apply`,
      { method: "POST", body: JSON.stringify(body) },
    ).then((res) => {
      emitServerNotices(res.exercise.notices);
      return res;
    }),
  /** Fichier de situation envoyé tel quel (KMZ, KML, CoT, paquet ATAK, NVG) : graphiques proposés à l'import. */
  analyzeGraphicsImport: (exerciseId: string, file: File) =>
    request<GraphicsImport>(`/exercises/${exerciseId}/graphics/import?${new URLSearchParams({ filename: file.name })}`, {
      method: "POST",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    }),
  // --- Enchaînement des ordres ---
  chain: (exerciseId: string) => request<ChainInfo>(`/exercises/${exerciseId}/chain`),
  /** Crée le dossier du subordonné, l'OPO du dossier rangé dans ses ordres reçus. */
  transmitOrder: (exerciseId: string, unit: string, opo: number) =>
    request<ChainInfo & { dossier: TransmittedDossier; notices?: string[] }>(`/exercises/${exerciseId}/chain`, {
      method: "POST",
      body: JSON.stringify({ unit, opo }),
    }),
  /** Code à usage unique qui remet le dossier du subordonné à un poste d'une autre licence (rendu une fois). */
  transferCode: (exerciseId: string, unit: string, opo: number) =>
    request<ChainInfo & { code: string; transfer_id: string; expires_at: string }>(`/exercises/${exerciseId}/chain/codes`, {
      method: "POST",
      body: JSON.stringify({ unit, opo }),
    }),
  revokeTransfer: (exerciseId: string, transferId: string) =>
    request<ChainInfo>(`/exercises/${exerciseId}/chain/codes/${transferId}`, { method: "DELETE" }),
  /** Avis du dossier transmis : l'ordre de son chef a changé depuis sa transmission (relu d'abord s'il a changé). */
  chainNotice: (exerciseId: string) => request<{ notice: ChiefNotice | null }>(`/exercises/${exerciseId}/chain/notice`),
  /** « Ignorer ce changement » : l'avis tombe, rien n'est reporté. */
  ignoreChainNotice: (exerciseId: string) =>
    request<{ notice: null }>(`/exercises/${exerciseId}/chain/notice/ignore`, { method: "POST" }),
  // --- Déclinaison de l'ordre aux subordonnés (docs/20 §13) ---
  declination: (exerciseId: string) => request<DeclinationState>(`/exercises/${exerciseId}/declination`),
  /** Lance la déclinaison (ou la reprend, ou la remet à jour) ; le travail suit, relu par `declination`. `opo` : OPO
   *  décliné (défaut du serveur : le n° 1). */
  startDeclination: (exerciseId: string, language: "fr" | "en", opo?: number) =>
    request<DeclinationState>(`/exercises/${exerciseId}/declination`, {
      method: "POST",
      body: JSON.stringify(opo ? { language, opo } : { language }),
    }),
  /** Dossier subordonné retiré de l'ordre de son chef : le détacher (dossier indépendant) ou le garder tel quel. */
  settleRemoved: (exerciseId: string, action: "detach" | "keep") =>
    request<DeclinationState>(`/exercises/${exerciseId}/declination/removed`, {
      method: "POST",
      body: JSON.stringify({ action }),
    }),
  groupedDocument: (exerciseId: string) =>
    request<GroupedDocument>(`/exercises/${exerciseId}/declination/document?format=json`),
  groupedDocumentUrl: (exerciseId: string, format: "pdf" | "docx" | "odt" | "txt") =>
    `/api/exercises/${exerciseId}/declination/document?format=${format}`,
  /** Croquis d'un subordonné (PNG) ; `version` : fin de la génération, pour ne pas garder un croquis périmé. */
  groupedSketchUrl: (exerciseId: string, childId: string, version: string | null) =>
    `/api/exercises/${exerciseId}/declination/document/map/${childId}?${new URLSearchParams({ v: version ?? "" })}`,
  previewTransfer: (code: string) =>
    request<TransferPreview>("/transfers/preview", { method: "POST", body: JSON.stringify({ code }) }),
  /** Reçoit le dossier remis par un code ; ses avis (zone de l'émetteur) sont annoncés et rangés dans le dossier rendu. */
  receiveTransfer: (code: string) =>
    request<{ dossier: TransmittedDossier; notices?: string[] }>("/transfers/receive", {
      method: "POST",
      body: JSON.stringify({ code }),
    }).then((res) => {
      emitServerNotices(res.notices);
      return { ...res, dossier: { ...res.dossier, notices: res.notices ?? [] } };
    }),
  // --- RETEX ---
  retex: (exerciseId: string) => request<Retex>(`/exercises/${exerciseId}/retex`),
  analyzeRetexText: (exerciseId: string, text: string, source?: string) =>
    request<RetexAnalysis>(`/exercises/${exerciseId}/retex/analyze`, {
      method: "POST",
      body: JSON.stringify({ text, source: source || null }),
    }),
  /** Fichier envoyé tel quel (PDF, Word, LibreOffice, RTF, texte) ; rien n'est gardé avant le versement. */
  analyzeRetexFile: (exerciseId: string, file: File) =>
    request<RetexAnalysis>(`/exercises/${exerciseId}/retex/analyze/file?${new URLSearchParams({ filename: file.name })}`, {
      method: "POST",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    }),
  addRetex: (exerciseId: string, eni: RetexEnemy, passages: string[], source: string) =>
    request<Retex & { added: number; dropped: number; eni: RetexEnemy }>(`/exercises/${exerciseId}/retex`, {
      method: "POST",
      body: JSON.stringify({ eni, passages, source }),
    }),
  deleteRetexEntry: (exerciseId: string, entryId: string) =>
    request<Retex>(`/exercises/${exerciseId}/retex/entries/${entryId}`, { method: "DELETE" }),
  clearRetex: (exerciseId: string, eni: RetexEnemy) =>
    request<Retex>(`/exercises/${exerciseId}/retex?${new URLSearchParams({ eni })}`, { method: "DELETE" }),
  logistics: (exerciseId: string, phase: Phase, opo?: number) =>
    request<Logistics>(withOpo(`/exercises/${exerciseId}/logistics`, opo, new URLSearchParams({ phase }))),
  /** `base` : date de modification des hypothèses lue (null : valeurs par défaut), contrôlée par le serveur (409). */
  updateLogisticsAssumptions: (
    exerciseId: string,
    phase: Phase,
    changes: Partial<LogisticsAssumptions> & { base?: string | null },
    opo?: number,
  ) =>
    request<Logistics>(withOpo(`/exercises/${exerciseId}/logistics/assumptions`, opo, new URLSearchParams({ phase })), {
      method: "PUT",
      body: JSON.stringify(changes),
    }),
  resetLogisticsAssumptions: (exerciseId: string, phase: Phase, opo?: number) =>
    request<Logistics>(withOpo(`/exercises/${exerciseId}/logistics/assumptions`, opo, new URLSearchParams({ phase })), {
      method: "DELETE",
    }),
  /** Plan d'emploi des drones d'un brouillon des Données initiales (chantier « Drones ») : indisponible en CENTAC, au
   *  corps, sans niveau ou sans ordre de bataille (`reason`). */
  dronesPreview: (body: DronesPreviewRequest) =>
    request<DronesPreview>("/drones/preview", { method: "POST", body: JSON.stringify(body) }),
  /** Calque « Drones » de la carte : VAA, ROZ drones et NAI de l'OPO `opo` (rotation CENTAC : collection vide). */
  droneAirspace: (exerciseId: string, opo?: number) =>
    request<DroneAirspace>(withOpo(`/exercises/${exerciseId}/drones/airspace`, opo)),
};
